import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
    FiX,
    FiChevronLeft,
    FiChevronRight,
    FiCheckCircle,
    FiXCircle,
    FiPlayCircle,
    FiHelpCircle,
    FiClipboard,
    FiLock,
    FiArrowLeft,
} from "react-icons/fi";
import AlertDialog from "../Components/Shared/AlertDialog";
import { API_BASE } from "../../../api";
import { ENDPOINTS as LMS_ENDPOINTS } from "../Utils/Endpoints";
import { Models } from "../Utils/ModelsLMS";
import { saveExamAttempt, getExamAttempts } from "../Utils/ExamResponseService";
import { saveQuizAttempt, getQuizAttempts } from "../Utils/QuizResponseService";
import {
    markLeccionAsCompleted,
    calculateCourseProgress,
    isLeccionCompleted,
    getCompletedLessons,
    getQuizzesStatus,
    getExamsStatus
} from "../Utils/ProgressService";
import { getSessionUserId } from "../../../utils/SessionUser";
import "../Styles/CoursePlayer.css";
import "../Styles/LmsExtra.css";

const formatUrl = (url) => {
    if (!url) return '';

    // YouTube
    const youtubeRegExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const ytMatch = url.match(youtubeRegExp);
    if (ytMatch && ytMatch[2].length === 11) {
        return `https://www.youtube.com/embed/${ytMatch[2]}`;
    }

    // Vimeo
    const vimeoRegExp = /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const vimeoMatch = url.match(vimeoRegExp);
    if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return url;
};

const CoursePlayerStudent = () => {
    const { slug, lessonId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const SERVER_URL = API_BASE.replace(/\/api\/?$/, "");

    const [course, setCourse] = useState(null);
    const [currentLessonId, setCurrentLessonId] = useState(null);
    const [justCompletedTitle, setJustCompletedTitle] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [lessonSections, setLessonSections] = useState([]);
    const [loadingLessonContent, setLoadingLessonContent] = useState(false);
    const [sidebarModules, setSidebarModules] = useState([]);

    // Estados para contenido actual (lección, cuestionario o examen)
    const [currentContentType, setCurrentContentType] = useState(null); // "LECCION", "CUESTIONARIO", "EXAMEN"
    const [currentContentId, setCurrentContentId] = useState(null);
    const [currentContentTitle, setCurrentContentTitle] = useState("");

    // Estados para cuestionarios y exámenes
    const [quizPreguntas, setQuizPreguntas] = useState([]);
    const [examPreguntas, setExamPreguntas] = useState([]);
    const [respuestasUsuario, setRespuestasUsuario] = useState({}); // { idPregunta: idOpcionSeleccionada }
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [quizSubmitted, setQuizSubmitted] = useState(false);
    const [quizResult, setQuizResult] = useState(null); // { total, contestadas, correctas, incorrectas }
    const [examSubmitted, setExamSubmitted] = useState(false);
    const [examResult, setExamResult] = useState(null);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [resultsData, setResultsData] = useState({
        correctas: 0,
        incorrectas: 0,
        calificacion: 0,
        aprobado: false
    });

    // Estados para guardado de respuestas
    const [savingResponses, setSavingResponses] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [idEstudiante, setIdEstudiante] = useState(null);
    const [navBlockMessage, setNavBlockMessage] = useState("");
    const [estudianteCursoRow, setEstudianteCursoRow] = useState(null);

    // Estados para control de límite de intentos
    const [currentExam, setCurrentExam] = useState(null); // Objeto completo del examen actual
    const [currentQuiz, setCurrentQuiz] = useState(null); // Objeto completo del cuestionario actual
    const [examAttempts, setExamAttempts] = useState(0); // Número de intentos realizados
    const [quizAttempts, setQuizAttempts] = useState(0); // Número de intentos realizados
    const [allAttempts, setAllAttempts] = useState({}); // { 'EXAMEN-5': 2, 'CUESTIONARIO-8': 1 }

    // Estados para progreso del curso
    const [courseProgress, setCourseProgress] = useState({ completadas: 0, total: 0, porcentaje: 0 });
    const [currentLessonCompleted, setCurrentLessonCompleted] = useState(false);
    const [completedVideos, setCompletedVideos] = useState(new Set());

    // Estados para resultado (aprobado/reprobado) basado en último intento
    const [quizStatus, setQuizStatus] = useState({ aprobados: [], reprobados: [] });
    const [examStatus, setExamStatus] = useState({ aprobados: [], reprobados: [] });

    const [completedKeys, setCompletedKeys] = useState([]);
    const [failedKeys, setFailedKeys] = useState([]);

    const [alertDialog, setAlertDialog] = useState({ open: false, title: '', message: '', variant: 'info' });

    // Cargar estructura del curso (módulos + todo el contenido) desde BD
    useEffect(() => {
        // console.log("[CoursePlayerStudent] params:", { slug, lessonId });

        const loadCourseStructure = async () => {
            const courseId = Number(slug);
            if (!courseId) {
                setCourse(null);
                setSidebarModules([]);
                setCurrentLessonId(null);
                return;
            }

            try {
                // 1. Cargar curso, módulos y todo el contenido en paralelo (Optimizado: solo datos del curso)
                const [cursoRes, modulosRes, leccionesRes, cuestionariosRes, examenesRes, moduloContenidoRes] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.cursosById(courseId)),
                    axios.get(LMS_ENDPOINTS.moduloByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.leccionesByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.cuestionarioByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.examenByCurso(courseId)),
                    axios.get(LMS_ENDPOINTS.moduloContenidoByCurso(courseId)),
                ]);

                // Curso
                const cursoDataRaw = cursoRes.data;
                const cursoEncontrado = Array.isArray(cursoDataRaw) ? cursoDataRaw[0] : cursoDataRaw;
                setCourse(cursoEncontrado || null);

                // Listas filtradas por el backend
                const modulosData = modulosRes.data || [];
                const leccionesData = leccionesRes.data || [];
                const cuestionariosData = cuestionariosRes.data || [];
                const examenesData = examenesRes.data || [];
                const moduloContenidoData = (moduloContenidoRes.data || []).map(Models.moduloContenido);

                const modulosDelCurso = modulosData.sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                // 2. Mapear módulos con su contenido ORDENADO según ModuloContenido
                const builtModules = modulosDelCurso.map((m) => {
                    const idModulo = m.idModulo || m.IdModulo;
                    const tituloModulo = m.nombre || m.Nombre || "Módulo";

                    // Obtener ModuloContenido de este módulo
                    const contenidosDelModulo = moduloContenidoData
                        .filter((mc) => mc.idModulo === idModulo && mc.activo)
                        .sort((a, b) => (a.orden || 0) - (b.orden || 0));

                    // Mapear cada contenido a su tipo específico
                    const contenidoMapeado = contenidosDelModulo
                        .map((mc) => {
                            const tipo = mc.tablaContenido;

                            if (tipo === "Lecciones") {
                                const leccion = leccionesData.find(
                                    (l) => (l.idLeccion || l.IdLeccion) === mc.idAsociado
                                );
                                if (!leccion) return null;

                                // Validar que la lección esté activa
                                const isActivo = leccion.activo ?? leccion.Activo ?? true;
                                if (!isActivo) return null;

                                return {
                                    tipo: "LECCION",
                                    id: leccion.idLeccion || leccion.IdLeccion,
                                    lessonId: leccion.idLeccion || leccion.IdLeccion,
                                    lessonTitle: leccion.titulo || leccion.Titulo || "Lección",
                                    moduleId: idModulo,
                                    monedas: leccion.monedas || leccion.Monedas || 0,
                                    completado: false,
                                };
                            }

                            if (tipo === "Cuestionario") {
                                const cuestionario = cuestionariosData.find(
                                    (q) => (q.idCuestionario || q.IdCuestionario) === mc.idAsociado
                                );
                                if (!cuestionario) return null;

                                // Validar que esté activo
                                const isActivo = cuestionario.activo ?? cuestionario.Activo ?? true;
                                if (!isActivo) return null;

                                return {
                                    tipo: "CUESTIONARIO",
                                    id: cuestionario.idCuestionario || cuestionario.IdCuestionario,
                                    quizId: cuestionario.idCuestionario || cuestionario.IdCuestionario,
                                    lessonTitle: cuestionario.nombre || cuestionario.Nombre || "Cuestionario",
                                    moduleId: idModulo,
                                    limiteIntentos: cuestionario.limiteIntentos || cuestionario.LimiteIntentos || 3,
                                    cuestionarioCompleto: cuestionario, // Guardar el objeto completo
                                };
                            }

                            if (tipo === "Examen") {
                                const examen = examenesData.find(
                                    (e) => (e.idExamen || e.IdExamen) === mc.idAsociado
                                );
                                if (!examen) return null;

                                // Validar que esté activo
                                const isActivo = examen.activo ?? examen.Activo ?? true;
                                if (!isActivo) return null;

                                return {
                                    tipo: "EXAMEN",
                                    id: examen.idExamen || examen.IdExamen,
                                    examId: examen.idExamen || examen.IdExamen,
                                    lessonTitle: examen.nombre || examen.Nombre || "Examen",
                                    moduleId: idModulo,
                                    limiteIntentos: examen.limiteIntentos || examen.LimiteIntentos || 3,
                                    examenCompleto: examen, // Guardar el objeto completo
                                };
                            }

                            return null;
                        })
                        .filter(Boolean);

                    return {
                        moduleId: idModulo,
                        moduleTitle: tituloModulo,
                        items: contenidoMapeado,
                    };
                });

                setSidebarModules(builtModules);

                // Seleccionar lección inicial (URL > primera lección disponible)
                let initialLessonId = lessonId || null;
                if (!initialLessonId) {
                    const firstModule = builtModules.find(
                        (m) => (m.items || []).some((item) => item.tipo === "LECCION")
                    );
                    if (firstModule) {
                        const firstLesson = firstModule.items.find((item) => item.tipo === "LECCION");
                        if (firstLesson) {
                            initialLessonId = firstLesson.lessonId;
                        }
                    }
                }
                setCurrentLessonId(initialLessonId);
            } catch (error) {
                console.error("[CoursePlayerStudent] Error al cargar estructura de curso:", error);
                setCourse(null);
                setSidebarModules([]);
                setCurrentLessonId(null);
            }
        };

        loadCourseStructure();
    }, [slug, lessonId]);

    // Cargar ID del estudiante (cadena: Usuario -> Persona -> Empleado -> Estudiante)
    useEffect(() => {
        const loadEstudianteId = async () => {
            const courseId = Number(slug);
            if (!courseId) return;

            try {
                const idUsuario = getSessionUserId();
                if (idUsuario) {
                    const [resUsuarios, resEmpleados, resEstudiantes] = await Promise.all([
                        axios.get(LMS_ENDPOINTS.usuarios),
                        axios.get(LMS_ENDPOINTS.empleados),
                        axios.get(LMS_ENDPOINTS.estudiante),
                    ]);

                    const usuarios = resUsuarios.data || [];
                    const empleados = resEmpleados.data || [];
                    const estudiantes = (resEstudiantes.data || []).map(Models.estudiante);

                    const usuario = usuarios.find((u) => Number(u.idUsuario || u.IdUsuario) === Number(idUsuario));
                    const idPersona = usuario?.idPersona ?? usuario?.IdPersona ?? null;

                    const empleado = empleados.find((e) => Number(e.idPersona || e.IdPersona) === Number(idPersona));
                    const idEmpleado = empleado?.idEmpleado ?? empleado?.IdEmpleado ?? null;

                    const estudiante = estudiantes.find((s) => Number(s.idEmpleado) === Number(idEmpleado));
                    const estudianteId = estudiante?.idEstudiante ?? estudiante?.IdEstudiante ?? null;

                    if (estudianteId) {
                        setIdEstudiante(estudianteId);
                        // console.log('[CoursePlayerStudent] ID Estudiante resuelto desde sesión:', {
                        //     idUsuario,
                        //     idPersona,
                        //     idEmpleado,
                        //     idEstudiante: estudianteId,
                        // });
                        return;
                    }

                    console.warn('[CoursePlayerStudent] No se pudo resolver idEstudiante desde sesión', {
                        idUsuario,
                        idPersona,
                        idEmpleado,
                    });
                }

                // Fallback: relación EstudianteCurso por curso (legado)
                const response = await axios.get(LMS_ENDPOINTS.estudiantesCurso);
                const relaciones = (response.data || []).map(Models.estudiantesCurso);
                const relacionCurso = relaciones.find(
                    r => (r.idCurso || r.IdCurso) === courseId
                );

                if (relacionCurso) {
                    const estudianteId = relacionCurso.idEstudiante || relacionCurso.IdEstudiante;
                    setIdEstudiante(estudianteId);
                    // console.log('[CoursePlayerStudent] ID Estudiante cargado desde EstudianteCurso (fallback):', estudianteId);
                    return;
                }

                console.warn('[CoursePlayerStudent] No se encontró relación estudiante-curso');
            } catch (error) {
                console.error('[CoursePlayerStudent] Error cargando ID estudiante:', error);
            }
        };

        loadEstudianteId();
    }, [slug]);

    // Cargar relación EstudianteCurso para poder persistir CalificacionFinal
    useEffect(() => {
        const loadEstudianteCursoRow = async () => {
            const courseId = Number(slug);
            if (!courseId || !idEstudiante) return;

            try {
                const response = await axios.get(LMS_ENDPOINTS.estudiantesCurso);
                const relaciones = (response.data || []).map(Models.estudiantesCurso);

                const relacionCurso = relaciones.find(
                    (r) => String(r.idCurso) === String(courseId) && String(r.idEstudiante) === String(idEstudiante)
                );

                setEstudianteCursoRow(relacionCurso || null);
            } catch (error) {
                console.error('[CoursePlayerStudent] Error cargando EstudianteCurso:', error);
                setEstudianteCursoRow(null);
            }
        };

        loadEstudianteCursoRow();
    }, [slug, idEstudiante]);

    const recalculateAndPersistFinalGrade = async () => {
        const courseId = Number(slug);
        if (!courseId || !idEstudiante) return;

        try {
            const examIds = Array.from(
                new Set(
                    (sidebarModules || [])
                        .flatMap((m) => m.items || [])
                        .filter((it) => it && it.tipo === 'EXAMEN')
                        .map((it) => it.examId || it.idExamen || it.id)
                        .filter(Boolean)
                        .map((x) => Number(x))
                )
            );

            if (examIds.length === 0) {
                return;
            }

            const res = await axios.get(LMS_ENDPOINTS.examenesIntentos);
            const intentos = (res.data || []).map(Models.examenesIntentos);

            const intentosEstudiante = intentos.filter(
                (i) => String(i.idEstudiante) === String(idEstudiante) && examIds.includes(Number(i.idExamen))
            );

            const latestByExam = {};
            intentosEstudiante.forEach((intento) => {
                const idExamen = Number(intento.idExamen);
                const fecha = new Date(intento.fechaIntento || intento.FechaIntento || intento.fecha || intento.Fecha || 0);
                const current = latestByExam[idExamen];
                if (!current) {
                    latestByExam[idExamen] = intento;
                    return;
                }
                const fechaCurrent = new Date(current.fechaIntento || current.FechaIntento || current.fecha || current.Fecha || 0);
                if (fecha > fechaCurrent) {
                    latestByExam[idExamen] = intento;
                }
            });

            const latestAttempts = Object.values(latestByExam);
            const totalExamenesCurso = examIds.length;
            const calificacionFinal = totalExamenesCurso === 0
                ? 0
                : Math.round(
                    latestAttempts.reduce((acc, it) => acc + (Number(it.calificacion) || 0), 0) / totalExamenesCurso
                );

            const payloadBase = {
                idEstudiantesCursos: estudianteCursoRow?.idEstudiantesCursos || 0,
                idEstudiante: Number(idEstudiante) || 0,
                idCurso: Number(courseId) || 0,
                calificacionFinal: Number(calificacionFinal) || 0,
                idEstado: estudianteCursoRow?.idEstado ?? 1,
                certificadoGenerado: Boolean(estudianteCursoRow?.certificadoGenerado),
                ultimoAcceso: estudianteCursoRow?.ultimoAcceso || new Date().toISOString(),
                activo: estudianteCursoRow?.activo ?? true,
            };

            if (estudianteCursoRow?.idEstudiantesCursos) {
                await axios.put(LMS_ENDPOINTS.estudiantesCurso, payloadBase);
                setEstudianteCursoRow((prev) => ({
                    ...(prev || {}),
                    calificacionFinal: payloadBase.calificacionFinal,
                }));
            } else {
                const respCreate = await axios.post(LMS_ENDPOINTS.estudiantesCurso, payloadBase);
                const created = respCreate?.data ? Models.estudiantesCurso(respCreate.data) : null;
                if (created) setEstudianteCursoRow(created);
            }
        } catch (error) {
            console.error('[CoursePlayerStudent] Error recalculando/persistiendo CalificacionFinal:', error);
        }
    };

    // Cargar todos los intentos de exámenes y cuestionarios del estudiante
    useEffect(() => {
        const loadAllAttempts = async () => {
            if (!idEstudiante || !sidebarModules || sidebarModules.length === 0) return;

            // console.log('[CoursePlayerStudent] Cargando todos los intentos del estudiante...');
            const attempts = {};

            try {
                // Obtener todos los exámenes y cuestionarios de los módulos
                const allItems = sidebarModules.flatMap(mod => mod.items || []);

                // Cargar intentos de exámenes
                const exams = allItems.filter(item => item.tipo === 'EXAMEN');
                for (const exam of exams) {
                    try {
                        const intentos = await getExamAttempts(exam.examId, idEstudiante);
                        const key = `EXAMEN-${exam.examId}`;
                        attempts[key] = intentos.length;
                        // console.log(`[CoursePlayerStudent] ${key}: ${intentos.length} intentos`);
                    } catch (error) {
                        console.error(`[CoursePlayerStudent] Error cargando intentos de examen ${exam.examId}:`, error);
                    }
                }

                // Cargar intentos de cuestionarios
                const quizzes = allItems.filter(item => item.tipo === 'CUESTIONARIO');
                for (const quiz of quizzes) {
                    try {
                        const intentos = await getQuizAttempts(quiz.quizId, idEstudiante);
                        const key = `CUESTIONARIO-${quiz.quizId}`;
                        attempts[key] = intentos.length;
                        // console.log(`[CoursePlayerStudent] ${key}: ${intentos.length} intentos`);
                    } catch (error) {
                        console.error(`[CoursePlayerStudent] Error cargando intentos de cuestionario ${quiz.quizId}:`, error);
                    }
                }

                setAllAttempts(attempts);
                // console.log('[CoursePlayerStudent] Todos los intentos cargados:', attempts);
            } catch (error) {
                console.error('[CoursePlayerStudent] Error general cargando intentos:', error);
            }
        };

        loadAllAttempts();
    }, [idEstudiante, sidebarModules]);

    // Marcar lecciones completadas en la estructura del sidebar (para palomita + verde)
    useEffect(() => {
        const hydrateCompletedLessons = async () => {
            if (!idEstudiante || !sidebarModules || sidebarModules.length === 0) return;

            try {
                const completadas = await getCompletedLessons(idEstudiante);
                const idsLeccionesCompletadas = new Set(
                    (completadas || []).map((le) => String(le.idLeccion || le.IdLeccion))
                );

                setSidebarModules((prev) => {
                    const next = (prev || []).map((mod) => ({
                        ...mod,
                        items: (mod.items || []).map((item) => {
                            if (!item || item.tipo !== 'LECCION') return item;
                            const isCompleted = idsLeccionesCompletadas.has(String(item.lessonId));
                            if (item.completado === isCompleted) return item;
                            return { ...item, completado: isCompleted };
                        }),
                    }));
                    return next;
                });

                const lessonKeys = Array.from(idsLeccionesCompletadas).map((id) => `LECCION-${id}`);
                setCompletedKeys((prev) => {
                    const prevNonLesson = (prev || []).filter((k) => !String(k).startsWith('LECCION-'));
                    return [...lessonKeys, ...prevNonLesson];
                });
            } catch (error) {
                console.error('[CoursePlayerStudent] Error hidratando lecciones completadas:', error);
            }
        };

        hydrateCompletedLessons();
    }, [idEstudiante, sidebarModules.length]);

    // Cargar estado aprobado/reprobado de exámenes y cuestionarios (último intento)
    useEffect(() => {
        const loadAssessmentsStatus = async () => {
            if (!idEstudiante) return;

            try {
                const [qs, es] = await Promise.all([
                    getQuizzesStatus(idEstudiante),
                    getExamsStatus(idEstudiante),
                ]);

                const normalizeStatus = (status) => {
                    const aprobados = (status?.aprobados || []).map((x) => String(x));
                    const reprobados = (status?.reprobados || []).map((x) => String(x));
                    return { aprobados, reprobados };
                };

                const normalizedQs = normalizeStatus(qs);
                const normalizedEs = normalizeStatus(es);

                // console.log('[CoursePlayerStudent] Status cuestionarios (normalizado):', normalizedQs);
                // console.log('[CoursePlayerStudent] Status examenes (normalizado):', normalizedEs);

                setQuizStatus(normalizedQs);
                setExamStatus(normalizedEs);

                const assessmentCompletedKeys = [
                    ...(normalizedQs.aprobados || []).map((id) => `CUESTIONARIO-${String(id)}`),
                    ...(normalizedEs.aprobados || []).map((id) => `EXAMEN-${String(id)}`),
                ];

                const assessmentFailedKeys = [
                    ...(normalizedQs.reprobados || []).map((id) => `CUESTIONARIO-${String(id)}`),
                    ...(normalizedEs.reprobados || []).map((id) => `EXAMEN-${String(id)}`),
                ];

                setCompletedKeys((prev) => {
                    const lessonOnly = (prev || []).filter((k) => String(k).startsWith('LECCION-'));
                    return [...lessonOnly, ...assessmentCompletedKeys];
                });
                setFailedKeys(assessmentFailedKeys);
            } catch (error) {
                console.error('[CoursePlayerStudent] Error cargando estado de evaluaciones:', error);
                setQuizStatus({ aprobados: [], reprobados: [] });
                setExamStatus({ aprobados: [], reprobados: [] });
                setFailedKeys([]);
            }
        };

        loadAssessmentsStatus();
    }, [idEstudiante]);

    const allVideosCompleted = useMemo(() => {
        // Si la lección ya está marcada como completada en la BD, permitimos pasar
        if (currentLessonCompleted) return true;

        // Buscar todos los IDs de videos en las secciones de la lección actual
        const videoIds = [];
        lessonSections.forEach(section => {
            (section.children || []).forEach(column => {
                (column.children || []).forEach(element => {
                    if (element.type === 'video' && element.src) {
                        videoIds.push(element.id);
                    }
                });
            });
        });

        // Si no hay videos, la validación pasa
        if (videoIds.length === 0) return true;

        // Verificar si todos los videos encontrados han sido registrados como completados
        return videoIds.every(id => completedVideos.has(id));
    }, [lessonSections, completedVideos, currentLessonCompleted]);

    const isCurrentActivityCompleted = useMemo(() => {
        if (!currentContentType) return true;

        if (currentContentType === 'LECCION') {
            return allVideosCompleted;
        }

        if (currentContentType === 'CUESTIONARIO') {
            return Boolean(quizSubmitted);
        }

        if (currentContentType === 'EXAMEN') {
            return Boolean(examSubmitted);
        }

        return true;
    }, [currentContentType, allVideosCompleted, quizSubmitted, examSubmitted]);

    const getCurrentContentPath = () => {
        if (!slug) return `/menu/lms/curso-estudiante/${slug}`;

        if (currentContentType === 'LECCION') {
            return `/menu/lms/curso-estudiante/${slug}/player/${currentContentId}`;
        }
        if (currentContentType === 'CUESTIONARIO') {
            return `/menu/lms/curso-estudiante/${slug}/player/${currentContentId}?type=quiz`;
        }
        if (currentContentType === 'EXAMEN') {
            return `/menu/lms/curso-estudiante/${slug}/player/${currentContentId}?type=exam`;
        }

        return `/menu/lms/curso-estudiante/${slug}`;
    };

    const showNavBlockMessage = (msg) => {
        setNavBlockMessage(msg);
        window.setTimeout(() => {
            setNavBlockMessage((prev) => (prev === msg ? "" : prev));
        }, 2500);
    };

    // Bloquear acceso a evaluaciones si el certificado ya fue generado
    useEffect(() => {
        if (!estudianteCursoRow || !estudianteCursoRow.certificadoGenerado) return;
        
        if (currentContentType === 'EXAMEN' || currentContentType === 'CUESTIONARIO') {
            showNavBlockMessage('No puedes acceder a evaluaciones porque ya has generado tu certificado.');
            navigate(`/menu/lms/curso-estudiante/${slug}`);
        }
    }, [estudianteCursoRow, currentContentType, navigate, slug]);

    // Detectar el parámetro type en la URL y cargar el contenido correspondiente
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const type = searchParams.get('type');

        console.log('[CoursePlayerStudent] useEffect ejecutado - lessonId:', lessonId, 'type:', type);

        // Si no hay lessonId, no hacer nada
        if (!lessonId) return;

        const targetType = type === 'quiz' ? 'CUESTIONARIO' : type === 'exam' ? 'EXAMEN' : 'LECCION';
        const isNavigationToDifferentItem =
            currentContentId &&
            (String(currentContentId) !== String(lessonId) || String(currentContentType) !== String(targetType));

        if (isNavigationToDifferentItem && !isCurrentActivityCompleted) {
            showNavBlockMessage('Debes terminar la actividad actual antes de continuar a la siguiente.');
            navigate(getCurrentContentPath());
            return;
        }

        if (type === 'quiz') {
            // Cargar cuestionario
            console.log('[CoursePlayerStudent] Cargando cuestionario con ID:', lessonId);
            setCurrentContentType('CUESTIONARIO');
            setCurrentContentId(lessonId);
            setCurrentContentTitle(''); // Se llenará al cargar
            setCurrentLessonId(null);
            setLessonSections([]);
            setRespuestasUsuario({});
            setQuizSubmitted(false);
            setQuizResult(null);
            setExamSubmitted(false); // Reset exam state too
            setExamResult(null);
            loadQuizQuestions(lessonId);

        } else if (type === 'exam') {
            // Cargar examen
            console.log('[CoursePlayerStudent] Cargando examen con ID:', lessonId);
            setCurrentContentType('EXAMEN');
            setCurrentContentId(lessonId);
            setCurrentContentTitle(''); // Se llenará al cargar
            setCurrentLessonId(null);
            setLessonSections([]);
            setRespuestasUsuario({});
            setExamSubmitted(false);
            setExamResult(null);
            setQuizSubmitted(false); // Reset quiz state too
            setQuizResult(null);
            loadExamQuestions(lessonId);

        } else if (!type) {
            // Si no hay parámetro type, es una lección normal
            console.log('[CoursePlayerStudent] Cargando lección con ID:', lessonId);
            setCurrentContentType('LECCION');
            setCurrentContentId(lessonId);
            setCurrentLessonId(lessonId);
            // Reset both
            setQuizSubmitted(false);
            setQuizResult(null);
            setExamSubmitted(false);
            setExamResult(null);
            setCompletedVideos(new Set()); // Limpiar videos completados al cambiar de lección
        }
    }, [lessonId, location.search, idEstudiante]);

    // Cargar progreso del curso
    useEffect(() => {
        const loadProgress = async () => {
            if (idEstudiante && course) {
                console.log('[CoursePlayerStudent] Cargando progreso del curso...');
                const progress = await calculateCourseProgress(idEstudiante, course);
                setCourseProgress(progress);
                console.log('[CoursePlayerStudent] Progreso cargado:', progress);
            }
        };
        loadProgress();
    }, [idEstudiante, course]);

    // Verificar si la lección actual está completada
    useEffect(() => {
        const checkLessonCompleted = async () => {
            if (currentLessonId && idEstudiante) {
                const completed = await isLeccionCompleted(currentLessonId, idEstudiante);
                setCurrentLessonCompleted(completed);
                console.log(`[CoursePlayerStudent] Lección ${currentLessonId} completada:`, completed);
            }
        };
        checkLessonCompleted();
    }, [currentLessonId, idEstudiante]);

    // Función para cargar preguntas de un cuestionario
    const loadQuizQuestions = async (cuestionarioId) => {
        console.log('[loadQuizQuestions] Iniciando carga para cuestionario:', cuestionarioId);
        setLoadingQuestions(true);

        try {
            // 1. Obtener datos del cuestionario
            const resCuestionarios = await axios.get(LMS_ENDPOINTS.cuestionario);
            const cuestionarios = resCuestionarios.data || [];
            const cuestionario = cuestionarios.find(
                q => (q.idCuestionario || q.IdCuestionario) === parseInt(cuestionarioId, 10)
            );

            // 2. Establecer el cuestionario actual y cargar sus intentos
            if (cuestionario) {
                setCurrentQuiz(cuestionario);
                if (idEstudiante) {
                    const intentos = await getQuizAttempts(cuestionarioId, idEstudiante);
                    const intentosRealizados = intentos.length;
                    setQuizAttempts(intentosRealizados);

                    const limiteIntentos = cuestionario.limiteIntentos || cuestionario.LimiteIntentos || 3;
                    console.log(`[loadQuizQuestions] Validando límite: ${intentosRealizados} de ${limiteIntentos} intentos`);

                    if (limiteIntentos > 0 && intentosRealizados >= limiteIntentos) {
                        console.warn('[loadQuizQuestions] ⚠️ LÍMITE DE INTENTOS ALCANZADO');
                        setLoadingQuestions(false);

                        setAlertDialog({
                            open: true,
                            title: 'Límite de intentos',
                            message: `Has alcanzado el límite de intentos para este cuestionario.\n\n` +
                                `Intentos realizados: ${intentosRealizados} de ${limiteIntentos}\n\n` +
                                `No puedes realizar más intentos.`,
                            variant: 'warning'
                        });

                        // Redirigir al curriculum del curso
                        navigate(`/menu/lms/curso-estudiante/${slug}`);
                        return;
                    }
                } else {
                    // Si no tenemos idEstudiante todavía, no cargamos preguntas para evitar bypass de validación
                    console.log('[loadQuizQuestions] idEstudiante no disponible, esperando...');
                    return;
                }
            }

            const resPreguntas = await axios.get(LMS_ENDPOINTS.cuestionariosPreguntas);
            const todasPreguntas = resPreguntas.data || [];

            let preguntasDelCuestionario = todasPreguntas.filter(
                (p) => (p.idCuestionario || p.IdCuestionario) === parseInt(cuestionarioId, 10)
            );

            // Lógica de aleatoriedad
            if (cuestionario) {
                const cantidadAleatoria = cuestionario.preguntasAleatorias || cuestionario.PreguntasAleatorias || 0;

                if (cantidadAleatoria > 0) {
                    console.log(`[loadQuizQuestions] Aplicando aleatoriedad: ${cantidadAleatoria} preguntas al azar.`);

                    // Mezclar las preguntas (Fisher-Yates Shuffle)
                    for (let i = preguntasDelCuestionario.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [preguntasDelCuestionario[i], preguntasDelCuestionario[j]] = [preguntasDelCuestionario[j], preguntasDelCuestionario[i]];
                    }

                    // Tomar las primeras N (o todas si N >= length)
                    const cantidadATomar = Math.min(cantidadAleatoria, preguntasDelCuestionario.length);
                    preguntasDelCuestionario = preguntasDelCuestionario.slice(0, cantidadATomar);
                } else {
                    // console.log('[loadQuizQuestions] Sin aleatoriedad (mostrar todas en orden).');
                }
            }

            // console.log('[loadQuizQuestions] Preguntas seleccionadas:', preguntasDelCuestionario.length);

            let todasRespuestasTextoLibre = [];
            let todasRespuestasVF = [];
            try {
                const [resTextoLibre, resVF] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.cuestionarioRespuestaTextoLibre),
                    axios.get(LMS_ENDPOINTS.cuestionarioRespuestaVerdaderoFalso)
                ]);
                todasRespuestasTextoLibre = (resTextoLibre.data || []).map(Models.cuestionarioRespuestaTextoLibre);
                todasRespuestasVF = (resVF.data || []).map(Models.cuestionarioRespuestaVerdaderoFalso);
                // console.log('[loadQuizQuestions] Respuestas texto libre:', todasRespuestasTextoLibre.length, '| VF:', todasRespuestasVF.length);
            } catch (err) {
                console.error('[loadQuizQuestions] Error cargando respuestas complementarias:', err);
            }

            const preguntasConOpciones = await Promise.all(
                preguntasDelCuestionario.map(async (pregunta) => {
                    const idPregunta = pregunta.idCuestionarioPreguntas || pregunta.IdCuestionarioPreguntas;
                    const idTipoPregunta = pregunta.idTipoPregunta || pregunta.IdTipoPregunta || 0;

                    // console.log(`[loadQuizQuestions] Procesando pregunta ${idPregunta}, tipo: ${idTipoPregunta}`);

                    try {
                        if (idTipoPregunta === 1) {
                            const respTextoLibre = todasRespuestasTextoLibre.find(
                                (r) => r.idCuestionarioPreguntas === idPregunta
                            ) || null;

                            // console.log(`[loadQuizQuestions] Pregunta ${idPregunta} - Respuesta texto libre:`, respTextoLibre);

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "textoLibre",
                                respuestaTextoLibre: respTextoLibre,
                                opciones: [],
                            };
                        } else if (idTipoPregunta === 3) {
                            const respVF = todasRespuestasVF.find(
                                (r) => r.idCuestionarioPreguntas === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "verdaderoFalso",
                                respuestaVerdaderoFalso: respVF,
                                opciones: [],
                            };
                        }

                        const resRespuestas = await axios.get(LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultiple);
                        const todasRespuestas = resRespuestas.data || [];

                        const respuesta = todasRespuestas.find(
                            (r) => (r.idCuestionarioPreguntas || r.IdCuestionarioPreguntas) === idPregunta
                        );

                        if (respuesta) {
                            const idRespuesta = respuesta.idRespuestaOpcionMultiple || respuesta.IdRespuestaOpcionMultiple;

                            const resOpciones = await axios.get(LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultipleOpciones);
                            const todasOpciones = resOpciones.data || [];

                            const opcionesOrdenadas = todasOpciones
                                .filter((o) => (o.idRespuestaOpcionMultiple || o.IdRespuestaOpcionMultiple) === idRespuesta)
                                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idOpcion || o.IdOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                texto: o.texto || o.Texto || "",
                                esCorrecta: o.esCorrecta !== undefined ? o.esCorrecta : (o.EsCorrecta || false),
                                orden: o.orden || o.Orden || idx + 1,
                            }));

                            // console.log(`[loadQuizQuestions] Pregunta ${idPregunta} - Opciones cargadas:`, opciones.length);

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "opcionMultiple",
                                opciones,
                                idRespuesta,
                                permiteMultiple: respuesta.permiteMultiple !== undefined ? respuesta.permiteMultiple : respuesta.PermiteMultiple,
                            };
                        }

                        return { ...pregunta, idPregunta: idPregunta, tipo: "opcionMultiple", opciones: [] };
                    } catch (error) {
                        console.error(`[loadQuizQuestions] Error cargando opciones para pregunta ${idPregunta}:`, error);
                        return { ...pregunta, idPregunta: idPregunta, opciones: [] };
                    }
                })
            );

            // console.log('[loadQuizQuestions] Preguntas procesadas:', preguntasConOpciones);
            setQuizPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error('[loadQuizQuestions] Error al cargar preguntas del cuestionario:', error);
            setQuizPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };

    const loadExamQuestions = async (examenId) => {
        // console.log('[loadExamQuestions] Iniciando carga para examen:', examenId);
        setLoadingQuestions(true);

        try {
            // Obtener el examen de la BD para poder usar LimiteIntentos y PreguntasAleatorias
            const resExamenes = await axios.get(LMS_ENDPOINTS.examen);
            const examenes = resExamenes.data || [];
            const examen = examenes.find(
                (e) => (e.idExamen || e.IdExamen) === parseInt(examenId, 10)
            ) || null;

            // 2. Establecer el examen actual y cargar sus intentos
            if (examen) {
                setCurrentExam(examen);
                if (idEstudiante) {
                    const intentos = await getExamAttempts(examenId, idEstudiante);
                    const intentosRealizados = intentos.length;
                    setExamAttempts(intentosRealizados);

                    const limiteIntentos = examen.limiteIntentos || examen.LimiteIntentos || 3;
                    // console.log(`[loadExamQuestions] Validando límite: ${intentosRealizados} de ${limiteIntentos} intentos`);

                    if (limiteIntentos > 0 && intentosRealizados >= limiteIntentos) {
                        console.warn('[loadExamQuestions] ⚠️ LÍMITE DE INTENTOS ALCANZADO');
                        setLoadingQuestions(false);

                        setAlertDialog({
                            open: true,
                            title: 'Límite de intentos',
                            message: `Has alcanzado el límite de intentos para este examen.\n\n` +
                                `Intentos realizados: ${intentosRealizados} de ${limiteIntentos}\n\n` +
                                `No puedes realizar más intentos.`,
                            variant: 'warning'
                        });

                        // Redirigir al curriculum del curso
                        navigate(`/menu/lms/curso-estudiante/${slug}`);
                        return;
                    }
                } else {
                    // Si no tenemos idEstudiante todavía, no cargamos preguntas para evitar bypass de validación
                    // console.log('[loadExamQuestions] idEstudiante no disponible, esperando...');
                    return;
                }
            }

            const resPreguntas = await axios.get(LMS_ENDPOINTS.examenPreguntas);
            const todasPreguntas = resPreguntas.data || [];

            let preguntasDelExamen = todasPreguntas.filter(
                (p) => (p.idExamen || p.IdExamen) === parseInt(examenId, 10)
            );

            // Lógica de aleatoriedad (igual que en cuestionarios)
            if (examen) {
                const cantidadAleatoria = examen.preguntasAleatorias || examen.PreguntasAleatorias || 0;

                if (cantidadAleatoria > 0) {
                    // console.log(`[loadExamQuestions] Aplicando aleatoriedad: ${cantidadAleatoria} preguntas al azar.`);

                    // Mezclar las preguntas (Fisher-Yates Shuffle)
                    for (let i = preguntasDelExamen.length - 1; i > 0; i--) {
                        const j = Math.floor(Math.random() * (i + 1));
                        [preguntasDelExamen[i], preguntasDelExamen[j]] = [preguntasDelExamen[j], preguntasDelExamen[i]];
                    }

                    // Tomar las primeras N (o todas si N >= length)
                    const cantidadATomar = Math.min(cantidadAleatoria, preguntasDelExamen.length);
                    preguntasDelExamen = preguntasDelExamen.slice(0, cantidadATomar);
                } else {
                    // console.log('[loadExamQuestions] Sin aleatoriedad (mostrar todas en orden).');
                }
            }

            // console.log('[loadExamQuestions] Preguntas encontradas:', preguntasDelExamen.length);

            let todasRespuestasTextoLibre = [];
            let todasRespuestasVF = [];
            try {
                const [resTextoLibre, resVF] = await Promise.all([
                    axios.get(LMS_ENDPOINTS.examenPreguntaTextoLibre),
                    axios.get(LMS_ENDPOINTS.examenPreguntaVerdaderoFalso)
                ]);
                todasRespuestasTextoLibre = (resTextoLibre.data || []).map(Models.examenPreguntaTextoLibre);
                todasRespuestasVF = (resVF.data || []).map(Models.examenPreguntaVerdaderoFalso);
                // console.log('[loadExamQuestions] Respuestas texto libre:', todasRespuestasTextoLibre.length, '| VF:', todasRespuestasVF.length);
            } catch (err) {
                console.error('[loadExamQuestions] Error cargando respuestas complementarias:', err);
            }

            const preguntasConOpciones = await Promise.all(
                preguntasDelExamen.map(async (pregunta) => {
                    const idPregunta = pregunta.idExamenPregunta || pregunta.IdExamenPregunta || pregunta.idExamenPreguntas || pregunta.IdExamenPreguntas;
                    const idTipoPregunta = pregunta.idTipoPregunta || pregunta.IdTipoPregunta || 0;

                    // console.log(`[loadExamQuestions] Procesando pregunta ${idPregunta}, tipo: ${idTipoPregunta}`);

                    try {
                        if (idTipoPregunta === 1) {
                            const respTextoLibre = todasRespuestasTextoLibre.find(
                                (r) => r.idExamenPregunta === idPregunta
                            ) || null;

                            // console.log(`[loadExamQuestions] Pregunta ${idPregunta} - Respuesta texto libre:`, respTextoLibre);

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "textoLibre",
                                respuestaTextoLibre: respTextoLibre,
                                opciones: [],
                            };
                        } else if (idTipoPregunta === 3) {
                            const respVF = todasRespuestasVF.find(
                                (r) => r.idExamenPregunta === idPregunta
                            ) || null;

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "verdaderoFalso",
                                respuestaVerdaderoFalso: respVF,
                                opciones: [],
                            };
                        }

                        const resRespuestas = await axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultiple);
                        const todasRespuestas = resRespuestas.data || [];

                        const respuesta = todasRespuestas.find(
                            (r) => (r.idExamenPregunta || r.IdExamenPregunta) === idPregunta
                        );

                        if (respuesta) {
                            const idRespuesta = respuesta.idExamenRespuestaOpcionMultiple || respuesta.IdExamenRespuestaOpcionMultiple;

                            // console.log(`[loadExamQuestions] Pregunta ${idPregunta} - ID Respuesta OM: ${idRespuesta}`);

                            const resOpciones = await axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones);
                            const todasOpciones = resOpciones.data || [];

                            const opcionesOrdenadas = todasOpciones
                                .filter((o) => (o.idExamenRespuestaOpcionMultiple || o.IdExamenRespuestaOpcionMultiple) === idRespuesta)
                                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));

                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idExamenOpcion || o.IdExamenOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                texto: o.texto || o.Texto || "",
                                esCorrecta: o.esCorrecta !== undefined ? o.esCorrecta : (o.EsCorrecta || false),
                                orden: o.orden || o.Orden || idx + 1,
                            }));

                            // console.log(`[loadExamQuestions] Pregunta ${idPregunta} - Opciones cargadas:`, opciones.length);

                            return {
                                ...pregunta,
                                idPregunta: idPregunta,
                                tipo: "opcionMultiple",
                                opciones,
                                idRespuesta,
                                permiteMultiple: respuesta.permiteMultiple !== undefined ? respuesta.permiteMultiple : respuesta.PermiteMultiple,
                            };
                        }

                        return { ...pregunta, idPregunta: idPregunta, tipo: "opcionMultiple", opciones: [] };
                    } catch (error) {
                        console.error(`[loadExamQuestions] Error cargando opciones para pregunta ${idPregunta}:`, error);
                        return { ...pregunta, idPregunta: idPregunta, opciones: [] };
                    }
                })
            );

            // console.log('[loadExamQuestions] Preguntas procesadas:', preguntasConOpciones);
            setExamPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error('[loadExamQuestions] Error al cargar preguntas del examen:', error);
            setExamPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };

    // Lista aplanada de lecciones (a partir de sidebarModules)
    const flatLessons = useMemo(
        () =>
            sidebarModules.flatMap((m) =>
                (m.items || [])
                    .filter((item) => item.tipo === "LECCION") // Solo lecciones para navegación
                    .map((item) => ({
                        ...item,
                        moduleTitle:
                            m.moduleTitle ||
                            (course ? course.nombre || course.Nombre || course.titulo : ""),
                    }))
            ),
        [sidebarModules, course]
    );

    // Agrupamos por módulo para sidebar (ya construido desde BD)
    const modulesWithLessons = useMemo(
        () => sidebarModules,
        [sidebarModules]
    );

    const flatSidebarItems = useMemo(
        () => (modulesWithLessons || []).flatMap((m) => m.items || []),
        [modulesWithLessons]
    );

    const totalElements = flatSidebarItems.length;

    const completedCount = useMemo(
        () => flatSidebarItems.filter((x) => x.completado).length,
        [flatSidebarItems]
    );

    const topProgress = useMemo(
        () =>
            totalElements === 0
                ? 0
                : Math.round((completedCount / totalElements) * 100),
        [completedCount, totalElements]
    );

    const currentIndex = useMemo(() => {
        const idx = flatLessons.findIndex(
            (x) => String(x.lessonId) === String(currentLessonId)
        );
        return idx === -1 ? 0 : idx;
    }, [flatLessons, currentLessonId]);

    const currentItem =
        flatLessons.length > 0 ? flatLessons[currentIndex] || null : null;


    const currentSidebarIndex = useMemo(() => {
        if (!currentContentType) return -1;

        return flatSidebarItems.findIndex((it) => {
            if (!it) return false;

            if (currentContentType === 'LECCION') {
                return it.tipo === 'LECCION' && String(it.lessonId) === String(currentContentId);
            }

            return it.tipo === currentContentType && String(it.id) === String(currentContentId);
        });
    }, [flatSidebarItems, currentContentType, currentContentId]);

    // Handlers - Manejar clic en cualquier tipo de contenido
    const handleClickContent = async (item, ignoreLock = false) => {
        const { tipo, id, lessonId, quizId, examId, lessonTitle, cuestionarioCompleto, examenCompleto } = item;

        const isDifferentItem =
            currentContentId &&
            (String(currentContentId) !== String(id) || String(currentContentType) !== String(tipo));

        // VALIDACIÓN DE SECUENCIA
        if (!ignoreLock && !isItemUnlocked(item)) {
            showNavBlockMessage('Debes completar las actividades anteriores antes de acceder a esta.');
            return;
        }

        setCurrentContentType(tipo);
        setCurrentContentId(id);
        setCurrentContentTitle(lessonTitle || "");
        // Limpiar respuestas y resultados anteriores
        setRespuestasUsuario({});
        setQuizSubmitted(false);
        setQuizResult(null);
        setExamSubmitted(false);
        setExamResult(null);

        if (tipo === "LECCION") {
            setCurrentLessonId(lessonId);
            navigate(`/menu/lms/curso-estudiante/${slug}/player/${lessonId}`);
        } else if (tipo === "CUESTIONARIO") {
            setCurrentLessonId(null);
            setLessonSections([]);

            // Guardar el cuestionario completo
            setCurrentQuiz(cuestionarioCompleto);

            // Obtener intentos del estudiante para este cuestionario
            if (idEstudiante && quizId) {
                try {
                    const intentos = await getQuizAttempts(quizId, idEstudiante);
                    setQuizAttempts(intentos.length);
                    // console.log(`[CoursePlayerStudent] Cuestionario ${quizId}: ${intentos.length} intentos de ${cuestionarioCompleto?.limiteIntentos || cuestionarioCompleto?.LimiteIntentos || 3}`);
                } catch (error) {
                    console.error('[CoursePlayerStudent] Error obteniendo intentos de cuestionario:', error);
                    setQuizAttempts(0);
                }
            }

            navigate(`/menu/lms/curso-estudiante/${slug}/player/${quizId}?type=quiz`);
            loadQuizQuestions(quizId);
        } else if (tipo === "EXAMEN") {
            setCurrentLessonId(null);
            setLessonSections([]);

            // Guardar el examen completo
            setCurrentExam(examenCompleto);

            // Obtener intentos del estudiante para este examen
            if (idEstudiante && examId) {
                try {
                    const intentos = await getExamAttempts(examId, idEstudiante);
                    setExamAttempts(intentos.length);
                    // console.log(`[CoursePlayerStudent] Examen ${examId}: ${intentos.length} intentos de ${examenCompleto?.limiteIntentos || examenCompleto?.LimiteIntentos || 3}`);
                } catch (error) {
                    console.error('[CoursePlayerStudent] Error obteniendo intentos de examen:', error);
                    setExamAttempts(0);
                }
            }

            navigate(`/menu/lms/curso-estudiante/${slug}/player/${examId}?type=exam`);
            loadExamQuestions(examId);
        }
    };

    // Mantener compatibilidad con navegación de lecciones
    const handleClickLesson = (id) => {
        const item = flatLessons.find(l => l.lessonId === id);
        if (item) {
            handleClickContent(item);
        }
    };

    const handleToggleComplete = async () => {
        if (!currentItem) return;

        const targetLessonId = currentItem.lessonId;

        // Buscar el item para obtener monedas
        let currentLessonItem = null;
        sidebarModules.forEach(mod => {
            const found = (mod.items || []).find(item =>
                item.tipo === "LECCION" && String(item.lessonId) === String(targetLessonId)
            );
            if (found) currentLessonItem = found;
        });

        const isCompleting = !currentLessonItem?.completado;
        const monedasALeccion = currentLessonItem?.monedas || 0;

        // Actualización optimista de la UI
        setSidebarModules((prev) => {
            const updated = prev.map((mod) => ({
                ...mod,
                items: (mod.items || []).map((item) =>
                    item.tipo === "LECCION" && String(item.lessonId) === String(targetLessonId)
                        ? { ...item, completado: !item.completado }
                        : item
                ),
            }));

            const flat = updated.flatMap((m) => m.items || []);
            const refreshed = flat.find(
                (x) => x.tipo === "LECCION" && String(x.lessonId) === String(targetLessonId)
            );

            if (refreshed?.lessonTitle) {
                setJustCompletedTitle(
                    refreshed.completado ? refreshed.lessonTitle : ""
                );
                if (refreshed.completado) {
                    setTimeout(() => setJustCompletedTitle(""), 4000);
                }
            }

            return updated;
        });

        // Sincronizar estado de completado actual para navegación inmediata
        if (String(currentLessonId) === String(targetLessonId)) {
            setCurrentLessonCompleted(isCompleting);
        }

        // console.log(`[CoursePlayer] Toggle lección ${targetLessonId} -> Completado: ${isCompleting}, Monedas disponibles: ${monedasALeccion}`, currentLessonItem);

        // Llamada al backend
        if (isCompleting && idEstudiante) {
            await markLeccionAsCompleted(targetLessonId, idEstudiante, monedasALeccion);
        }
    };

    const goPrev = () => {
        if (currentSidebarIndex <= 0) return;
        const prev = flatSidebarItems[currentSidebarIndex - 1];
        if (prev) {
            handleClickContent(prev, true); // Pasar true para ignorar el bloqueo ya que acabamos de completar la actual
        }
    };

    const goNext = async () => {
        // Verificar si debemos completar la lección actual antes de avanzar
        const currentItem = flatSidebarItems[currentSidebarIndex];

        if (currentItem && currentItem.tipo === "LECCION" && !currentItem.completado && idEstudiante) {
            // console.log('[CoursePlayer] Avanzando y completando lección actual automáticamente...');
            const monedas = currentItem.monedas || 0;

            try {
                // 1. Guardar en backend (esperar a que termine)
                await markLeccionAsCompleted(currentItem.lessonId, idEstudiante, monedas);

                // 2. Actualizar estado local para reflejar el cambio inmediato
                setSidebarModules((prev) => {
                    return prev.map((mod) => ({
                        ...mod,
                        items: (mod.items || []).map((item) =>
                            item.tipo === "LECCION" && String(item.lessonId) === String(currentItem.lessonId)
                                ? { ...item, completado: true }
                                : item
                        ),
                    }));
                });
                setCurrentLessonCompleted(true);

                // Recargar progreso (sin await para no bloquear navegación)
                if (course) {
                    calculateCourseProgress(idEstudiante, course).then(p => setCourseProgress(p));
                }
            } catch (error) {
                console.error("Error completando lección al avanzar:", error);
            }
        }

        // Navegación
        if (currentSidebarIndex >= flatSidebarItems.length - 1) {
            // console.log('[CoursePlayerStudent] Última actividad completada, redirigiendo al curriculum...');
            navigate(`/menu/lms/curso-estudiante/${slug}`);
            return;
        }

        // Verificar si la siguiente actividad pertenece a otro módulo
        const currentItemObj = flatSidebarItems[currentSidebarIndex];
        const nextItem = flatSidebarItems[currentSidebarIndex + 1];



        // Avanzar a la siguiente actividad dentro del mismo módulo
        if (nextItem) handleClickContent(nextItem, true); // Pass true to ignore lock
    };

    // Cargar contenido de la lección actual desde BD (solo lectura)
    useEffect(() => {
        const targetLessonId = lessonId || currentLessonId;
        // console.log("[CoursePlayerStudent] currentItem:", currentItem, "targetLessonId:", targetLessonId);
        const loadLessonContent = async () => {
            if (!targetLessonId) {
                setLessonSections([]);
                return;
            }

            setLoadingLessonContent(true);
            try {
                const response = await axios.get(LMS_ENDPOINTS.leccionesContenido);
                const allContent = response.data || [];
                // console.log("[CoursePlayerStudent] total registros leccionesContenido:", allContent.length);
                const lessonContent = allContent.filter(
                    (c) =>
                        String(c.idLeccion || c.IdLeccion) === String(targetLessonId) &&
                        (c.activo === true || c.Activo === true || c.activo === undefined && c.Activo === undefined)
                );

                // console.log(
                //     "[CoursePlayerStudent] registros para idLeccion",
                //     targetLessonId,
                //     "->",
                //     lessonContent.length
                // );

                const sections = lessonContent
                    .map((item) => {
                        try {
                            const parsed = JSON.parse(item.valor || item.Valor);
                            return {
                                ...parsed,
                                id: item.idContenido || item.IdContenido,
                            };
                        } catch (e) {
                            console.error("Error parsing lesson content valor:", e);
                            return null;
                        }
                    })
                    .filter(Boolean)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));

                setLessonSections(sections);
                // console.log("[CoursePlayerStudent] sections mapeadas:", sections);
            } catch (error) {
                console.error("Error loading lesson content for player:", error);
                setLessonSections([]);
            } finally {
                setLoadingLessonContent(false);
            }
        };

        loadLessonContent();
    }, [lessonId, currentLessonId]);

    // Cargar preguntas de cuestionario
    /* FUNCIONES VIEJAS - REEMPLAZADAS POR LAS NUEVAS ARRIBA
    const loadQuizQuestions = async (quizId) => {
        setLoadingQuestions(true);
        try {
            // Cargar preguntas del cuestionario
            const resPreguntas = await axios.get(LMS_ENDPOINTS.cuestionariosPreguntas);
            const todasPreguntas = resPreguntas.data || [];
 
            const preguntasDelCuestionario = todasPreguntas.filter(
                (p) => (p.idCuestionario || p.IdCuestionario) === parseInt(quizId, 10)
            );
 
            // Cargar opciones para cada pregunta
            const preguntasConOpciones = await Promise.all(
                preguntasDelCuestionario.map(async (pregunta) => {
                    const idPregunta =
                        pregunta.idCuestionarioPreguntas ||
                        pregunta.IdCuestionarioPreguntas ||
                        pregunta.idCuestionarioPregunta ||
                        pregunta.IdCuestionarioPregunta;
 
                    try {
                        const resRespuestas = await axios.get(LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultiple);
                        const todasRespuestas = resRespuestas.data || [];
 
                        const respuesta = todasRespuestas.find(
                            (r) =>
                                (r.idCuestionarioPreguntas ||
                                    r.IdCuestionarioPreguntas ||
                                    r.idCuestionarioPregunta ||
                                    r.IdCuestionarioPregunta) === idPregunta
                        );
 
                        if (respuesta) {
                            const idRespuesta =
                                respuesta.idRespuestaOpcionMultiple ||
                                respuesta.IdRespuestaOpcionMultiple ||
                                respuesta.idRespuesta ||
                                respuesta.IdRespuesta;
 
                            const resOpciones = await axios.get(LMS_ENDPOINTS.cuestionarioRespuestaOpcionMultipleOpciones);
                            const todasOpciones = resOpciones.data || [];
 
                            const opcionesOrdenadas = todasOpciones
                                .filter(
                                    (o) =>
                                        (o.idRespuestaOpcionMultiple ||
                                            o.IdRespuestaOpcionMultiple) === idRespuesta
                                )
                                .sort((a, b) => (a.orden || a.Orden || 0) - (b.orden || b.Orden || 0));
 
                            const opciones = opcionesOrdenadas.map((o, idx) => ({
                                ...o,
                                idOpcion: o.idOpcion || o.IdOpcion,
                                llave: String.fromCharCode(65 + idx),
                                descripcion: o.texto || o.Texto || "",
                                esCorrecta: o.esCorrecta !== undefined ? o.esCorrecta : o.EsCorrecta !== undefined ? o.EsCorrecta : false,
                                orden: o.orden || o.Orden || idx + 1,
                            }));
 
                            return {
                                ...pregunta,
                                idPregunta,
                                permiteMultiple: respuesta.permiteMultiple || respuesta.PermiteMultiple || false,
                                descripcion: pregunta.descripcionPregunta || pregunta.DescripcionPregunta || "",
                                opciones,
                                idRespuesta,
                            };
                        }
 
                        return { ...pregunta, idPregunta, opciones: [] };
                    } catch (error) {
                        console.error(`Error cargando opciones para pregunta ${idPregunta}:`, error);
                        return { ...pregunta, idPregunta, opciones: [] };
                    }
                })
            );
 
            setQuizPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error("Error al cargar preguntas del cuestionario:", error);
            setQuizPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };
 
    // Cargar preguntas de examen
    const loadExamQuestions = async (examId) => {
        setLoadingQuestions(true);
        try {
            const [resPreguntas, resCabecerasOM, resOpcionesOM] = await Promise.all([
                axios.get(LMS_ENDPOINTS.examenPreguntas),
                axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultiple),
                axios.get(LMS_ENDPOINTS.examenPreguntaOpcionMultipleOpciones),
            ]);
 
            const todasPreguntas = (resPreguntas.data || []).map(Models.examenPreguntas);
            const todasCabecerasOM = (resCabecerasOM.data || []).map(Models.examenPreguntaOpcionMultiple);
            const todasOpcionesOM = (resOpcionesOM.data || []).map(Models.examenPreguntaOpcionMultipleOpcion);
 
            const preguntasDelExamen = todasPreguntas.filter(
                (p) => p.idExamen === parseInt(examId, 10)
            );
 
            const preguntasConOpciones = preguntasDelExamen.map((pregunta) => {
                const idPregunta = pregunta.idExamenPregunta;
 
                const cabecera = todasCabecerasOM.find(
                    (r) => r.idExamenPregunta === idPregunta
                );
 
                if (!cabecera) {
                    return { ...pregunta, opciones: [], idRespuesta: null };
                }
 
                const idRespuestaOM = cabecera.idExamenRespuestaOpcionMultiple;
 
                const opcionesOrdenadas = todasOpcionesOM
                    .filter((o) => o.idExamenRespuestaOpcionMultiple === idRespuestaOM)
                    .sort((a, b) => (a.orden || 0) - (b.orden || 0));
 
                const opciones = opcionesOrdenadas.map((o, idx) => ({
                    ...o,
                    // Aseguramos un identificador estable para la opción
                    idOpcion:
                        o.idOpcion ||
                        o.IdOpcion ||
                        o.idExamenPreguntaOpcionMultiple ||
                        o.IdExamenPreguntaOpcionMultiple ||
                        `${idPregunta}-${idx}`,
                    llave: String.fromCharCode(65 + idx),
                    descripcion: o.texto || "",
                    esCorrecta:
                        o.esCorrecta !== undefined
                            ? o.esCorrecta
                            : o.EsCorrecta !== undefined
                                ? o.EsCorrecta
                                : false,
                    orden: o.orden || idx + 1,
                }));
 
                return {
                    ...pregunta,
                    idPregunta,
                    permiteMultiple: cabecera.permiteMultiple || cabecera.PermiteMultiple || false,
                    descripcion: pregunta.descripcionPregunta || pregunta.DescripcionPregunta || "",
                    opciones,
                    idRespuesta: idRespuestaOM,
                };
            });
 
            setExamPreguntas(preguntasConOpciones);
        } catch (error) {
            console.error("Error al cargar preguntas del examen:", error);
            setExamPreguntas([]);
        } finally {
            setLoadingQuestions(false);
        }
    };
 
    */ // FIN DE FUNCIONES VIEJAS
    // Renderer de solo lectura para las secciones de la lección
    const renderLessonSections = () => {
        if (loadingLessonContent) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando contenido de la lección...
                </div>
            );
        }

        if (!lessonSections || lessonSections.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Esta lección aún no tiene contenido configurado.
                </div>
            );
        }

        return lessonSections.map((section, index) => (
            <div
                key={section.id}
                style={{
                    marginBottom: 0,
                    backgroundColor: section.style?.backgroundColor || "#ffffff",
                    padding: section.style?.padding ?? 20,
                    borderRadius: index === 0
                        ? (index === lessonSections.length - 1 ? "8px" : "8px 8px 0 0")
                        : (index === lessonSections.length - 1 ? "0 0 8px 8px" : "0"),
                }}
            >
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            section.columns === 2
                                ? `${section.splitRatio || 50}fr ${100 - (section.splitRatio || 50)}fr`
                                : "1fr",
                        gap: section.style?.gap ?? 20,
                    }}
                >
                    {(section.children || []).map((column, colIdx) => (
                        <div key={colIdx} style={{
                            minHeight: 40,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                            {(column.children || []).length === 0 && (
                                <div
                                    style={{
                                        textAlign: "center",
                                        color: "#cbd5f5",
                                        fontSize: "0.85rem",
                                        padding: 16,
                                    }}
                                >
                                    {/* Columna vacía oculta en vista estudiante */}
                                </div >
                            )}
                            {
                                (column.children || []).map((element) => (
                                    <div
                                        key={element.id}
                                        style={{
                                            ...element.style,
                                            height: (element.type === 'text' || element.type === 'h1' || element.type === 'h2') ? 'auto' : element.style?.height,
                                            border: "none",
                                            boxSizing: "border-box",
                                            display: 'flex',
                                            justifyContent: 'center',
                                            width: '100%'
                                        }}
                                    >
                                        {element.type === "text" ||
                                            element.type === "h1" ||
                                            element.type === "h2" ? (
                                            <div
                                                style={{
                                                    userSelect: 'none',
                                                    overflowWrap: 'break-word',
                                                    wordWrap: 'break-word',
                                                    maxWidth: '100%',
                                                    whiteSpace: 'normal'
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: (element.content || "").replace(
                                                        /\n/g,
                                                        "<br />"
                                                    ),
                                                }}
                                            />
                                        ) : element.type === "image" ? (
                                            <img
                                                src={element.src}
                                                alt="Contenido de la lección"
                                                style={{
                                                    maxWidth: "100%",
                                                    height: element.style?.height || "auto",
                                                    objectFit: "cover",
                                                    borderRadius: 8,
                                                }}
                                            />
                                        ) : element.type === "video" ? (
                                            element.src ? (
                                                <video
                                                    src={
                                                        element.src.startsWith("http")
                                                            ? element.src
                                                            : `${SERVER_URL}${element.src}`
                                                    }
                                                    controls
                                                    controlsList="nodownload"
                                                    onContextMenu={(e) => e.preventDefault()}
                                                    onEnded={() => {
                                                        // console.log(`[Video] Finalizado: ${element.id}`);
                                                        setCompletedVideos(prev => {
                                                            const next = new Set(prev);
                                                            next.add(element.id);
                                                            return next;
                                                        });
                                                    }}
                                                    style={{
                                                        width: element.style?.width || "100%",
                                                        height: element.style?.height || 300,
                                                        borderRadius: 8,
                                                        backgroundColor: "#000",
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        backgroundColor: "#000",
                                                        height: element.style?.height || 300,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#fff",
                                                        borderRadius: 8,
                                                    }}
                                                >
                                                    <span style={{ fontSize: "1.2rem" }}>
                                                        Video no configurado
                                                    </span>
                                                </div>
                                            )
                                        ) : element.type === "iframe" ? (
                                            element.src ? (
                                                <iframe
                                                    src={formatUrl(element.src)}
                                                    title={element.content || "Contenido externo"}
                                                    style={{
                                                        width: element.style?.width || "100%",
                                                        height: element.style?.height || "400px",
                                                        border: "none",
                                                        borderRadius: 8,
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        backgroundColor: "#f3f4f6",
                                                        height: element.style?.height || 400,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        color: "#6b7280",
                                                        borderRadius: 8,
                                                    }}
                                                >
                                                    <span style={{ fontSize: "1.2rem" }}>
                                                        Sitio web no configurado
                                                    </span>
                                                </div>
                                            )
                                        ) : null}
                                    </div>
                                ))
                            }
                        </div >
                    ))}
                </div >
            </div >
        ));
    };

    const handleSelectOption = (idPregunta, idOpcion, isMultiple = false) => {
        setRespuestasUsuario((prev) => {
            const currentSelection = prev[idPregunta];

            if (isMultiple) {
                // Si es selección múltiple, manejamos un array
                let newSelection = Array.isArray(currentSelection) ? [...currentSelection] : [];

                // Si ya está seleccionado, lo quitamos
                if (newSelection.includes(idOpcion)) {
                    newSelection = newSelection.filter(id => String(id) !== String(idOpcion));
                } else {
                    // Si no, lo agregamos
                    newSelection.push(idOpcion);
                }

                return {
                    ...prev,
                    [idPregunta]: newSelection,
                };
            } else {
                // Selección única (comportamiento anterior)
                return {
                    ...prev,
                    [idPregunta]: idOpcion,
                };
            }
        });
    };

    const handleQuizButtonClick = () => {
        if (!quizSubmitted) {
            handleSubmitQuiz();
        } else {
            const aprobado = resultsData.aprobado;
            const limiteIntentos = currentQuiz?.limiteIntentos || currentQuiz?.LimiteIntentos || 3;
            const intentosRestantes = limiteIntentos - quizAttempts;

            // Avanzar SI Y SOLO SI aprobaste O agotaste los intentos
            if (aprobado || intentosRestantes <= 0) {
                navigateToNextAfterSubmit('CUESTIONARIO', currentContentId);
            } else {
                // Volver a intentar (refrescar el examen/cuestionario)
                setQuizSubmitted(false);
                setQuizResult(null);
                setRespuestasUsuario({});
                loadQuizQuestions(currentContentId);
            }
        }
    };

    const handleExamButtonClick = () => {
        if (!examSubmitted) {
            handleSubmitExam();
        } else {
            const aprobado = resultsData.aprobado;
            const limiteIntentos = currentExam?.limiteIntentos || currentExam?.LimiteIntentos || 3;
            const intentosRestantes = limiteIntentos - examAttempts;

            // Avanzar SI Y SOLO SI aprobaste O agotaste los intentos
            if (aprobado || intentosRestantes <= 0) {
                navigateToNextAfterSubmit('EXAMEN', currentContentId);
            } else {
                // Volver a intentar (refrescar el examen)
                setExamSubmitted(false);
                setExamResult(null);
                setRespuestasUsuario({});
                loadExamQuestions(currentContentId);
            }
        }
    };

    const navigateToNextAfterSubmit = (tipoActual, idActual) => {
        // Usar flatSidebarItems para encontrar el índice actual
        const currentIdx = flatSidebarItems.findIndex((it) => {
            if (!it) return false;
            if (tipoActual === 'LECCION') {
                return it.tipo === 'LECCION' && String(it.lessonId) === String(idActual);
            }
            return it.tipo === tipoActual && String(it.id) === String(idActual);
        });

        if (currentIdx === -1) {
            navigate(`/menu/lms/curso-estudiante/${slug}`);
            return;
        }

        // Si es la última actividad del curso, ir al currículum
        if (currentIdx >= flatSidebarItems.length - 1) {
            navigate(`/menu/lms/curso-estudiante/${slug}`);
            return;
        }

        // Verificar si la siguiente actividad pertenece a otro módulo
        const currentItem = flatSidebarItems[currentIdx];
        const nextItem = flatSidebarItems[currentIdx + 1];

        // Se ha habilitado el flujo continuo entre módulos (eliminado bloqueo de cambio de módulo)

        if (!nextItem) {
            navigate(`/menu/lms/curso-estudiante/${slug}`);
            return;
        }

        // Navegar según el tipo
        if (nextItem.tipo === 'LECCION') {
            const nextLessonId = nextItem.lessonId || nextItem.id;
            handleClickContent(nextItem, true); // Pass true to ignore lock
            return;
        }

        if (nextItem.tipo === 'CUESTIONARIO') {
            const nextQuizId = nextItem.quizId || nextItem.id;
            handleClickContent(nextItem, true); // Pass true to ignore lock
            return;
        }

        if (nextItem.tipo === 'EXAMEN') {
            const nextExamId = nextItem.examId || nextItem.id;
            handleClickContent(nextItem, true); // Pass true to ignore lock
            return;
        }

        navigate(`/menu/lms/curso-estudiante/${slug}`);
    };

    const handleSubmitQuiz = async () => {
        if (!quizPreguntas || quizPreguntas.length === 0) return;

        // Validar que tengamos el ID del estudiante
        if (!idEstudiante) {
            setAlertDialog({
                open: true,
                title: 'Error de Identificación',
                message: 'Error: No se pudo identificar al estudiante. Por favor, recarga la página.',
                variant: 'error'
            });
            console.error('[CoursePlayerStudent] No hay idEstudiante disponible');
            return;
        }

        // Validar que tengamos el ID del cuestionario
        if (!currentContentId) {
            setAlertDialog({
                open: true,
                title: 'Error de Datos',
                message: 'Error: No se pudo identificar el cuestionario. Por favor, recarga la página.',
                variant: 'error'
            });
            console.error('[CoursePlayerStudent] No hay currentContentId (quizId) disponible');
            return;
        }

        let correctas = 0;
        let contestadas = 0;

        // Calcular resultados (incluyendo preguntas de texto libre)
        quizPreguntas.forEach((pregunta) => {
            const idPregunta = pregunta.idPregunta;

            // Verificar si es pregunta con espacios en blanco
            const descripcion = pregunta.descripcionPregunta || pregunta.DescripcionPregunta || pregunta.descripcion || '';
            const tieneEspaciosBlanco = descripcion.includes('{}');

            if (tieneEspaciosBlanco) {
                // Contar respuestas de espacios en blanco
                let tieneRespuesta = false;
                const respuestasBlanco = [];

                const blanksCount = descripcion.split('{}').length - 1;
                for (let i = 0; i < blanksCount; i++) {

                    const respuesta = respuestasUsuario[`${idPregunta}_blank_${i}`] || "";
                    if (respuesta && respuesta.trim()) {
                        tieneRespuesta = true;
                    }
                    respuestasBlanco.push(respuesta);
                }

                if (tieneRespuesta) {
                    contestadas += 1;

                    // Verificar si es correcta
                    let esCorrecta = false;

                    // Obtener respuestas correctas
                    let respuestasCorrectas = [];

                    if (pregunta.respuestaTextoLibre) {
                        const palabrasClave = pregunta.respuestaTextoLibre.palabrasClave ||
                            pregunta.respuestaTextoLibre.PalabrasClave || '';
                        if (palabrasClave) {
                            respuestasCorrectas = [palabrasClave];
                        }
                    }

                    // Si no hay respuestas de texto libre, buscar en opciones correctas
                    if (respuestasCorrectas.length === 0 && pregunta.opciones) {
                        respuestasCorrectas = pregunta.opciones
                            .filter(o => o.esCorrecta || o.EsCorrecta || o.correcta)
                            .map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || '');
                    }

                    // Comparar respuestas (case-insensitive y trimmed)
                    if (respuestasCorrectas.length > 0) {
                        esCorrecta = true;
                        for (let i = 0; i < respuestasBlanco.length; i++) {
                            const respuestaUsuario = (respuestasBlanco[i] || '').trim().toLowerCase();
                            const respuestaCorrecta = (respuestasCorrectas[i] || respuestasCorrectas[0] || '').trim().toLowerCase();

                            if (respuestaUsuario !== respuestaCorrecta) {
                                esCorrecta = false;
                                break;
                            }
                        }
                    } else {
                        // Si no hay respuestas correctas definidas, marcar como correcta
                        esCorrecta = true;
                    }

                    if (esCorrecta) {
                        correctas += 1;
                    }
                }
            } else if (pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && pregunta.idTipoPregunta === 3)) {
                const val = respuestasUsuario[idPregunta];
                if (val !== undefined && val !== null) {
                    contestadas += 1;
                    const correcta = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta;
                    if (String(val) === String(correcta)) {
                        correctas += 1;
                    }
                }
            } else {
                // Pregunta de opción múltiple
                const userSelection = respuestasUsuario[idPregunta];

                // Verificar si se contestó (no vacío/nulo)
                const hasAnswer = Array.isArray(userSelection) ? userSelection.length > 0 : !!userSelection;

                if (!hasAnswer) return;
                contestadas += 1;

                const permiteMultiple = pregunta.permiteMultiple;

                if (permiteMultiple) {
                    // Lógica estricta para selección múltiple:
                    // Deben estar TODAS las correctas seleccionadas y NINGUNA incorrecta.

                    const opciones = pregunta.opciones || [];
                    const idsCorrectos = opciones
                        .filter(o => o.esCorrecta || o.EsCorrecta)
                        .map(o => String(o.idOpcion || o.IdOpcion));

                    const idsUsuario = Array.isArray(userSelection)
                        ? userSelection.map(String)
                        : [String(userSelection)];

                    // Verificar cantidad exacta
                    if (idsCorrectos.length !== idsUsuario.length) {
                        // Si la cantidad difiere, ya está mal
                    } else {
                        // Verificar que cada ID usuario esté en correctos
                        const todasCorrectas = idsUsuario.every(id => idsCorrectos.includes(id));
                        if (todasCorrectas) {
                            correctas += 1;
                        }
                    }

                } else {
                    // Selección única
                    const opciones = pregunta.opciones || [];
                    const seleccionada = opciones.find(
                        (o) =>
                            String(o.idOpcion || o.IdOpcion) ===
                            String(userSelection)
                    );

                    if (!seleccionada) return;

                    const esCorrecta =
                        seleccionada.esCorrecta !== undefined
                            ? seleccionada.esCorrecta
                            : seleccionada.EsCorrecta !== undefined
                                ? seleccionada.EsCorrecta
                                : false;

                    if (esCorrecta) {
                        correctas += 1;
                    }
                }
            }
        });

        const incorrectas = contestadas - correctas;
        const resultado = {
            total: quizPreguntas.length,
            contestadas,
            correctas,
            incorrectas,
        };

        setQuizResult(resultado);
        setQuizSubmitted(true);

        // Guardar en base de datos
        setSavingResponses(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            // console.log('[CoursePlayerStudent] Guardando respuestas del cuestionario...');
            // console.log('Datos:', {
            //     idCuestionario: currentContentId,
            //     idEstudiante,
            //     totalPreguntas: quizPreguntas.length,
            //     resultado
            // });

            const saveResult = await saveQuizAttempt({
                idCuestionario: currentContentId,
                idEstudiante: idEstudiante,
                preguntas: quizPreguntas,
                respuestasUsuario: respuestasUsuario,
                resultado: resultado
            });

            // console.log('[CoursePlayerStudent] Respuestas guardadas exitosamente:', saveResult);
            setSaveSuccess(true);

            // Mostrar resultados con Modal
            const calificacionVal = saveResult.calificacion || 0;
            const aprobadoCalc = Math.round(calificacionVal) >= 60; // REGLA: 60% aprueba (redondeado)

            setResultsData({
                correctas: resultado.correctas,
                incorrectas: resultado.incorrectas,
                calificacion: Math.round(calificacionVal),
                aprobado: aprobadoCalc
            });
            setShowResultsModal(true);

            // Incrementar el contador de intentos

            // Si hubo errores parciales, mostrarlos
            if (saveResult.errores && saveResult.errores.length > 0) {
                console.warn('[CoursePlayerStudent] Algunos errores al guardar:', saveResult.errores);
            }

            // Incrementar el contador de intentos
            setQuizAttempts(prev => prev + 1);

            // Actualizar el estado global de intentos
            const key = `CUESTIONARIO-${currentContentId}`;
            setAllAttempts(prev => ({
                ...prev,
                [key]: (prev[key] || 0) + 1
            }));

            // Recargar progreso del curso
            if (course) {
                const progress = await calculateCourseProgress(idEstudiante, course);
                setCourseProgress(progress);
            }

            // NO navegar automáticamente, dejar que el usuario haga clic en "Siguiente"
            // navigateToNextAfterSubmit('CUESTIONARIO', currentContentId);

        } catch (error) {
            console.error('[CoursePlayerStudent] Error al guardar respuestas:', error);
            setSaveError(error.message || 'Error desconocido al guardar las respuestas');

            setAlertDialog({
                open: true,
                title: 'Error al Guardar',
                message: `Error al guardar las respuestas del cuestionario.\n\n` +
                    `Los resultados se muestran a continuación, pero NO fueron guardados:\n\n` +
                    `Preguntas totales: ${resultado.total}\n` +
                    `Contestadas: ${resultado.contestadas}\n` +
                    `Correctas: ${resultado.correctas}\n` +
                    `Incorrectas: ${resultado.incorrectas}\n\n` +
                    `Error: ${error.message || 'Error desconocido'}\n\n` +
                    `Por favor, contacta al administrador.`,
                variant: 'error'
            });
        } finally {
            setSavingResponses(false);
        }
    };


    const handleSubmitExam = async () => {
        if (!examPreguntas || examPreguntas.length === 0) return;

        // Validar que tengamos el ID del estudiante
        if (!idEstudiante) {
            setAlertDialog({
                open: true,
                title: 'Error de Identificación',
                message: 'Error: No se pudo identificar al estudiante. Por favor, recarga la página.',
                variant: 'error'
            });
            console.error('[CoursePlayerStudent] No hay idEstudiante disponible');
            return;
        }

        // Validar que tengamos el ID del examen
        if (!currentContentId) {
            setAlertDialog({
                open: true,
                title: 'Error de Datos',
                message: 'Error: No se pudo identificar el examen. Por favor, recarga la página.',
                variant: 'error'
            });
            console.error('[CoursePlayerStudent] No hay currentContentId (examId) disponible');
            return;
        }

        let correctas = 0;
        let contestadas = 0;

        // Calcular resultados (incluyendo preguntas de texto libre)
        examPreguntas.forEach((pregunta) => {
            const idPregunta = pregunta.idPregunta;

            // Verificar si es pregunta con espacios en blanco
            const descripcion = pregunta.descripcionPregunta || pregunta.DescripcionPregunta || pregunta.descripcion || '';
            const tieneEspaciosBlanco = descripcion.includes('{}');

            if (tieneEspaciosBlanco) {
                // Contar respuestas de espacios en blanco
                let tieneRespuesta = false;
                const respuestasBlanco = [];

                const blanksCount = descripcion.split('{}').length - 1;
                for (let i = 0; i < blanksCount; i++) {

                    const respuesta = respuestasUsuario[`${idPregunta}_blank_${i}`] || "";
                    if (respuesta && respuesta.trim()) {
                        tieneRespuesta = true;
                    }
                    respuestasBlanco.push(respuesta);
                }

                if (tieneRespuesta) {
                    contestadas += 1;

                    // Verificar si es correcta
                    let esCorrecta = false;

                    // Obtener respuestas correctas
                    let respuestasCorrectas = [];

                    if (pregunta.respuestaTextoLibre) {
                        const palabrasClave = pregunta.respuestaTextoLibre.palabrasClave ||
                            pregunta.respuestaTextoLibre.PalabrasClave || '';
                        if (palabrasClave) {
                            respuestasCorrectas = [palabrasClave];
                        }
                    }

                    // Si no hay respuestas de texto libre, buscar en opciones correctas
                    if (respuestasCorrectas.length === 0 && pregunta.opciones) {
                        respuestasCorrectas = pregunta.opciones
                            .filter(o => o.esCorrecta || o.EsCorrecta || o.correcta)
                            .map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || '');
                    }

                    // Comparar respuestas (case-insensitive y trimmed)
                    if (respuestasCorrectas.length > 0) {
                        esCorrecta = true;
                        for (let i = 0; i < respuestasBlanco.length; i++) {
                            const respuestaUsuario = (respuestasBlanco[i] || '').trim().toLowerCase();
                            const respuestaCorrecta = (respuestasCorrectas[i] || respuestasCorrectas[0] || '').trim().toLowerCase();

                            if (respuestaUsuario !== respuestaCorrecta) {
                                esCorrecta = false;
                                break;
                            }
                        }
                    } else {
                        // Si no hay respuestas correctas definidas, marcar como correcta
                        esCorrecta = true;
                    }

                    if (esCorrecta) {
                        correctas += 1;
                    }
                }
            } else if (pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && pregunta.idTipoPregunta === 3)) {
                const val = respuestasUsuario[idPregunta];
                if (val !== undefined && val !== null) {
                    contestadas += 1;
                    const correcta = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta;
                    if (String(val) === String(correcta)) {
                        correctas += 1;
                    }
                }
            } else {
                // Pregunta de opción múltiple
                const userSelection = respuestasUsuario[idPregunta];

                // Verificar si se contestó (no vacío/nulo)
                const hasAnswer = Array.isArray(userSelection) ? userSelection.length > 0 : !!userSelection;

                if (!hasAnswer) return;
                contestadas += 1;

                const permiteMultiple = pregunta.permiteMultiple;

                if (permiteMultiple) {
                    // Lógica estricta para selección múltiple:
                    // Deben estar TODAS las correctas seleccionadas y NINGUNA incorrecta.

                    const opciones = pregunta.opciones || [];
                    const idsCorrectos = opciones
                        .filter(o => o.esCorrecta || o.EsCorrecta)
                        .map(o => String(o.idOpcion || o.IdOpcion));

                    const idsUsuario = Array.isArray(userSelection)
                        ? userSelection.map(String)
                        : [String(userSelection)];

                    // Verificar cantidad exacta
                    if (idsCorrectos.length !== idsUsuario.length) {
                        // Si la cantidad difiere, ya está mal
                    } else {
                        // Verificar que cada ID usuario esté en correctos
                        const todasCorrectas = idsUsuario.every(id => idsCorrectos.includes(id));
                        if (todasCorrectas) {
                            correctas += 1;
                        }
                    }

                } else {
                    // Selección única
                    const opciones = pregunta.opciones || [];
                    const seleccionada = opciones.find(
                        (o) =>
                            String(o.idOpcion || o.IdOpcion) ===
                            String(userSelection)
                    );

                    if (!seleccionada) return;

                    const esCorrecta =
                        seleccionada.esCorrecta !== undefined
                            ? seleccionada.esCorrecta
                            : seleccionada.EsCorrecta !== undefined
                                ? seleccionada.EsCorrecta
                                : false;

                    if (esCorrecta) {
                        correctas += 1;
                    }
                }
            }
        });

        const incorrectas = contestadas - correctas;
        const resultado = {
            total: examPreguntas.length,
            contestadas,
            correctas,
            incorrectas,
        };

        setExamResult(resultado);
        setExamSubmitted(true);

        // Guardar en base de datos
        setSavingResponses(true);
        setSaveError(null);
        setSaveSuccess(false);

        try {
            // console.log('[CoursePlayerStudent] Guardando respuestas del examen...');
            // console.log('Datos:', {
            //     idExamen: currentContentId,
            //     idEstudiante,
            //     totalPreguntas: examPreguntas.length,
            //     resultado
            // });

            const saveResult = await saveExamAttempt({
                idExamen: parseInt(currentContentId, 10),
                idEstudiante: idEstudiante,
                preguntas: examPreguntas,
                respuestasUsuario: respuestasUsuario,
                resultado: resultado
            });

            await recalculateAndPersistFinalGrade();

            // console.log('[CoursePlayerStudent] ✅ Examen guardado correctamente:', saveResult);
            setSaveSuccess(true);
            setSaveError(null);

            // Mostrar resultados con Modal
            const calificacionVal = saveResult.calificacion || 0;
            const aprobadoCalc = Math.round(calificacionVal) >= 60; // REGLA: 60% aprueba (redondeado)

            setResultsData({
                correctas: resultado.correctas,
                incorrectas: resultado.incorrectas,
                calificacion: Math.round(calificacionVal),
                aprobado: aprobadoCalc
            });
            setShowResultsModal(true);

            // Incrementar el contador de intentos

            // Si hubo errores parciales, mostrarlos
            if (saveResult.errores && saveResult.errores.length > 0) {
                console.warn('[CoursePlayerStudent] Algunos errores al guardar:', saveResult.errores);
            }

            // Incrementar el contador de intentos
            setExamAttempts(prev => prev + 1);

            // Actualizar el estado global de intentos
            const key = `EXAMEN-${currentContentId}`;
            setAllAttempts(prev => ({
                ...prev,
                [key]: (prev[key] || 0) + 1
            }));

            // Recargar progreso del curso
            if (course) {
                const progress = await calculateCourseProgress(idEstudiante, course);
                setCourseProgress(progress);
            }

            // NO navegar automáticamente, dejar que el usuario haga clic en "Siguiente"
            // navigateToNextAfterSubmit('EXAMEN', currentContentId);

        } catch (error) {
            console.error('[CoursePlayerStudent] Error al guardar respuestas:', error);
            setSaveError(error.message || 'Error desconocido al guardar las respuestas');

            setAlertDialog({
                open: true,
                title: 'Error al Guardar',
                message: `Error al guardar las respuestas del examen.\n\n` +
                    `Los resultados se muestran a continuación, pero NO fueron guardados:\n\n` +
                    `Preguntas totales: ${resultado.total}\n` +
                    `Contestadas: ${resultado.contestadas}\n` +
                    `Correctas: ${resultado.correctas}\n` +
                    `Incorrectas: ${resultado.incorrectas}\n\n` +
                    `Error: ${error.message || 'Error desconocido'}\n\n` +
                    `Por favor, contacta al administrador.`,
                variant: 'error'
            });
        } finally {
            setSavingResponses(false);
        }
    };

    // Función helper para renderizar preguntas con espacios en blanco
    const renderQuestionWithBlanks = (descripcion, idPregunta, preguntaCompleta) => {
        // console.log('=== DEBUG renderQuestionWithBlanks ===');
        // console.log('Descripción:', descripcion);
        // console.log('ID Pregunta:', idPregunta);
        // console.log('Pregunta completa:', preguntaCompleta);

        // Si no hay {}, renderizar normalmente
        if (!descripcion.includes('{}')) {
            return <p style={{ margin: "0 0 0.75rem 0", color: "#0f172a" }}>{descripcion}</p>;
        }

        // Dividir la descripción por {}
        const parts = descripcion.split('{}');
        const blanksCount = parts.length - 1;

        // console.log('Número de espacios en blanco:', blanksCount);

        // Obtener las respuestas correctas
        let correctAnswers = [];

        // Primero intentar obtener de respuestaTextoLibre (preguntas de texto libre)
        if (preguntaCompleta && preguntaCompleta.respuestaTextoLibre) {
            const palabrasClave = preguntaCompleta.respuestaTextoLibre.palabrasClave ||
                preguntaCompleta.respuestaTextoLibre.PalabrasClave ||
                "";
            // console.log('Palabras clave encontradas:', palabrasClave);

            if (palabrasClave) {
                // Si hay múltiples espacios en blanco, dividir las palabras clave
                // Asumimos que están separadas por algún delimitador o es una sola respuesta
                correctAnswers = [palabrasClave];
            }
        }

        // Si no hay respuestas de texto libre, buscar en opciones (opción múltiple)
        if (correctAnswers.length === 0 && preguntaCompleta && preguntaCompleta.opciones) {
            // console.log('Buscando en opciones:', preguntaCompleta.opciones);
            correctAnswers = preguntaCompleta.opciones
                .filter(o => o.esCorrecta || o.EsCorrecta || o.correcta)
                .map(o => o.descripcion || o.Descripcion || o.texto || o.Texto || "");
        }

        // console.log('Respuestas correctas finales:', correctAnswers);

        // Console log para debugging - mostrar pregunta con respuestas
        let preguntaConRespuestas = descripcion;
        correctAnswers.forEach((respuesta, idx) => {
            preguntaConRespuestas = preguntaConRespuestas.replace('{}', `[${respuesta}]`);
        });
        // console.log(`[Pregunta ID: ${idPregunta}] ${preguntaConRespuestas}`);

        return (
            <div style={{ margin: "0 0 0.75rem 0", color: "#0f172a", lineHeight: "2" }}>
                {parts.map((part, idx) => {
                    // Determinar qué valor mostrar en el input
                    const userAnswer = respuestasUsuario[`${idPregunta}_blank_${idx}`] || "";

                    // Obtener la respuesta correcta para este blank con fallback al primero si solo hay uno definido
                    const correctAnswer = (correctAnswers[idx] || correctAnswers[0] || "").trim().toLowerCase();
                    const maxLength = correctAnswer.length > 0 ? correctAnswer.length + 10 : 50;

                    // Determinar si la respuesta es correcta (comparación case-insensitive y trimmed)
                    // Si no hay respuestas correctas definidas (correctAnswers.length === 0), se considera correcta por defecto para coincidir con el backend
                    const isCorrect = correctAnswers.length === 0 || userAnswer.trim().toLowerCase() === correctAnswer;
                    const isSubmitted = quizSubmitted || examSubmitted;

                    // Determinar el color del borde según si es correcto o no
                    let borderColor = "#cbd5e1"; // Default
                    let backgroundColor = "#ffffff";

                    if (isSubmitted && userAnswer.trim()) {
                        borderColor = isCorrect ? "#10b981" : "#ef4444"; // Verde si correcto, rojo si incorrecto
                        backgroundColor = isCorrect ? "#f0fdf4" : "#fef2f2"; // Fondo verde claro o rojo claro
                    } else if (isSubmitted) {
                        // Si no contestó, pero se considera correcta por falta de definición
                        if (isCorrect && correctAnswers.length === 0) {
                            borderColor = "#10b981";
                            backgroundColor = "#f0fdf4";
                        } else {
                            backgroundColor = "#f1f5f9";
                        }
                    }

                    // Determinar qué valor mostrar en el input
                    let displayValue = userAnswer;

                    return (
                        <React.Fragment key={idx}>
                            <span>{part}</span>
                            {idx < blanksCount && (
                                <input
                                    type="text"
                                    style={{
                                        display: "inline-block",
                                        minWidth: "120px",
                                        padding: "4px 8px",
                                        margin: "0 4px",
                                        border: `2px solid ${borderColor}`,
                                        borderRadius: "4px",
                                        fontSize: "0.9rem",
                                        backgroundColor: backgroundColor,
                                        transition: "all 0.3s ease",
                                    }}
                                    placeholder="Escribe aquí..."
                                    maxLength={isSubmitted ? 200 : maxLength}
                                    value={displayValue}
                                    onChange={(e) => {
                                        if (!quizSubmitted && !examSubmitted) {
                                            setRespuestasUsuario(prev => ({
                                                ...prev,
                                                [`${idPregunta}_blank_${idx}`]: e.target.value
                                            }));
                                        }
                                    }}
                                    disabled={quizSubmitted || examSubmitted}
                                    title={""}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    const renderQuizView = () => {
        if (loadingQuestions) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando preguntas del cuestionario...
                </div>
            );
        }

        if (!quizPreguntas || quizPreguntas.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Este cuestionario aún no tiene preguntas configuradas.
                </div>
            );
        }

        return (
            <div style={{ marginTop: "1.5rem" }}>
                {quizPreguntas.map((pregunta, index) => {
                    const descripcion =
                        pregunta.descripcionPregunta ||
                        pregunta.DescripcionPregunta ||
                        pregunta.descripcion ||
                        "";

                    return (
                        <div key={pregunta.idPregunta || index} className="cc-question-card">
                            <div className="cc-question-header">
                                <h3 className="cc-question-number">
                                    Pregunta {index + 1}
                                    {pregunta.permiteMultiple && (
                                        <span style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: "normal", marginLeft: "8px" }}>
                                            (más de una respuesta)
                                        </span>
                                    )}
                                </h3>
                            </div>

                            <div className="cc-question-text">
                                {renderQuestionWithBlanks(descripcion, pregunta.idPregunta, pregunta)}
                            </div>

                            {/* Verdadero / Falso */}
                            {(pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && pregunta.idTipoPregunta === 3)) && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                                    {[true, false].map((val) => {
                                        const isTruth = val === true;
                                        const labelText = isTruth ? "Verdadero" : "Falso";
                                        const labelIcon = isTruth ? "✓" : "✗";

                                        const userAns = respuestasUsuario[pregunta.idPregunta];
                                        const isSelected = String(userAns) === String(val);

                                        const correctAnswer = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta;
                                        const isCorrectAnswer = String(correctAnswer) === String(val);

                                        let containerClass = "cc-option-item";
                                        let styleOverride = { cursor: 'pointer', flex: 1 };

                                        // Estilo para letra
                                        let letterStyle = {};

                                        if (quizSubmitted) {
                                            styleOverride.cursor = 'default';
                                        }

                                        // Feedback V/F
                                        if (quizSubmitted) {
                                            if (isCorrectAnswer) {
                                                containerClass += " cc-option-correct";
                                            } else if (isSelected) {
                                                styleOverride.background = "#fef2f2";
                                                styleOverride.borderColor = "#ef4444";
                                                letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                            }
                                        } else if (isSelected) {
                                            // Durante el intento, mostrar verde si está seleccionado
                                            containerClass += " cc-option-correct";
                                        }

                                        return (
                                            <div
                                                key={String(val)}
                                                className={containerClass}
                                                style={styleOverride}
                                                onClick={() => !quizSubmitted && handleSelectOption(pregunta.idPregunta, val)}
                                            >
                                                <span className="cc-option-letter" style={letterStyle}>{labelIcon}</span>
                                                <span className="cc-option-text" style={{ fontWeight: '600' }}>{labelText}</span>

                                                {quizSubmitted && isSelected && isCorrectAnswer && (
                                                    <span className="cc-correct-badge">✓ Correcta</span>
                                                )}
                                                {quizSubmitted && isSelected && !isCorrectAnswer && (
                                                    <span className="cc-correct-badge" style={{ background: '#ef4444' }}>✗ Incorrecta</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Opciones Múltiples */}
                            <div className="cc-options-list">
                                {(pregunta.opciones || []).map((opcion, idx) => {
                                    const llave =
                                        opcion.llave ||
                                        opcion.Llave ||
                                        String.fromCharCode(65 + idx);
                                    const desc =
                                        opcion.descripcion ||
                                        opcion.Descripcion ||
                                        opcion.texto ||
                                        opcion.Texto ||
                                        "";
                                    const esCorrecta =
                                        opcion.esCorrecta !== undefined
                                            ? opcion.esCorrecta
                                            : opcion.EsCorrecta !== undefined
                                                ? opcion.EsCorrecta
                                                : false;

                                    const idPregunta = pregunta.idPregunta;
                                    const idOpcion = opcion.idOpcion || opcion.IdOpcion;
                                    const selectedId = respuestasUsuario[idPregunta];
                                    const permiteMultiple = pregunta.permiteMultiple;

                                    let isSelected = false;
                                    if (permiteMultiple && Array.isArray(selectedId)) {
                                        isSelected = selectedId.some(id => String(id) === String(idOpcion));
                                    } else {
                                        isSelected = selectedId && String(selectedId) === String(idOpcion);
                                    }

                                    let containerClass = "cc-option-item";
                                    let styleOverride = { cursor: 'pointer' };
                                    let letterStyle = {};

                                    if (quizSubmitted) { // Note: For renderExamView this is 'examSubmitted'
                                        styleOverride.cursor = 'default';
                                    }

                                    // Feedback Opción Múltiple
                                    if (quizSubmitted) {
                                        if (esCorrecta) {
                                            containerClass += " cc-option-correct";
                                        } else if (isSelected) {
                                            styleOverride.background = "#fef2f2";
                                            styleOverride.borderColor = "#ef4444";
                                            letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                        }
                                    } else if (isSelected) {
                                        // Durante el intento, mostrar verde si está seleccionado
                                        containerClass += " cc-option-correct";
                                    }

                                    return (
                                        <div
                                            key={opcion.idOpcion || opcion.IdOpcion || idx}
                                            className={containerClass}
                                            style={styleOverride}
                                            onClick={() =>
                                                !quizSubmitted && // Note: For renderExamView change to examSubmitted
                                                idPregunta &&
                                                idOpcion &&
                                                handleSelectOption(idPregunta, idOpcion, permiteMultiple)
                                            }
                                        >
                                            <span className="cc-option-letter" style={letterStyle}>{llave}</span>
                                            <span className="cc-option-text">{desc}</span>
                                            {quizSubmitted && isSelected && esCorrecta && (
                                                <span className="cc-correct-badge">✓ Correcta</span>
                                            )}
                                            {quizSubmitted && isSelected && !esCorrecta && (
                                                <span className="cc-correct-badge" style={{ background: '#ef4444' }}>✗ Incorrecta</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderExamView = () => {
        if (loadingQuestions) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#64748b" }}>
                    Cargando preguntas del examen...
                </div>
            );
        }

        if (!examPreguntas || examPreguntas.length === 0) {
            return (
                <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                    Este examen aún no tiene preguntas configuradas.
                </div>
            );
        }

        return (
            <div style={{ marginTop: "1.5rem" }}>
                {examPreguntas.map((pregunta, index) => {
                    const descripcion =
                        pregunta.descripcionPregunta ||
                        pregunta.DescripcionPregunta ||
                        pregunta.descripcion ||
                        "";

                    return (
                        <div key={pregunta.idPregunta || index} className="cc-question-card">
                            <div className="cc-question-header">
                                <h3 className="cc-question-number">
                                    Pregunta {index + 1}
                                    {pregunta.permiteMultiple && (
                                        <span style={{ color: "#64748b", fontSize: "0.85rem", fontWeight: "normal", marginLeft: "8px" }}>
                                            (más de una respuesta)
                                        </span>
                                    )}
                                </h3>
                            </div>

                            <div className="cc-question-text">
                                {renderQuestionWithBlanks(descripcion, pregunta.idPregunta, pregunta)}
                            </div>

                            {/* Verdadero / Falso */}
                            {(pregunta.tipo === "verdaderoFalso" || (pregunta.idTipoPregunta && pregunta.idTipoPregunta === 3)) && (
                                <div style={{ display: 'flex', gap: '10px', marginTop: '1rem' }}>
                                    {[true, false].map((val) => {
                                        const isTruth = val === true;
                                        const labelText = isTruth ? "Verdadero" : "Falso";
                                        const labelIcon = isTruth ? "✓" : "✗";

                                        const userAns = respuestasUsuario[pregunta.idPregunta];
                                        const isSelected = String(userAns) === String(val);

                                        const correctAnswer = pregunta.respuestaVerdaderoFalso?.respuestaCorrecta;
                                        const isCorrectAnswer = String(correctAnswer) === String(val);

                                        let containerClass = "cc-option-item";
                                        let styleOverride = { cursor: 'pointer', flex: 1 };
                                        let letterStyle = {};

                                        if (examSubmitted) {
                                            styleOverride.cursor = 'default';
                                        }

                                        // Feedback Examen V/F
                                        if (examSubmitted) {
                                            if (isCorrectAnswer) {
                                                containerClass += " cc-option-correct";
                                            } else if (isSelected) {
                                                styleOverride.background = "#fef2f2";
                                                styleOverride.borderColor = "#ef4444";
                                                letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                            }
                                        } else if (isSelected) {
                                            // Durante el intento, mostrar verde si está seleccionado
                                            containerClass += " cc-option-correct";
                                        }

                                        return (
                                            <div
                                                key={String(val)}
                                                className={containerClass}
                                                style={styleOverride}
                                                onClick={() => !examSubmitted && handleSelectOption(pregunta.idPregunta, val)}
                                            >
                                                <span className="cc-option-letter" style={letterStyle}>{labelIcon}</span>
                                                <span className="cc-option-text" style={{ fontWeight: '600' }}>{labelText}</span>

                                                {examSubmitted && isSelected && isCorrectAnswer && (
                                                    <span className="cc-correct-badge">✓ Correcta</span>
                                                )}
                                                {examSubmitted && isSelected && !isCorrectAnswer && (
                                                    <span className="cc-correct-badge" style={{ background: '#ef4444' }}>✗ Incorrecta</span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Opciones Múltiples */}
                            <div className="cc-options-list">
                                {(pregunta.opciones || []).map((opcion, idx) => {
                                    const llave =
                                        opcion.llave ||
                                        opcion.Llave ||
                                        String.fromCharCode(65 + idx);
                                    const desc =
                                        opcion.descripcion ||
                                        opcion.Descripcion ||
                                        opcion.texto ||
                                        opcion.Texto ||
                                        "";
                                    const esCorrecta =
                                        opcion.esCorrecta !== undefined
                                            ? opcion.esCorrecta
                                            : opcion.EsCorrecta !== undefined
                                                ? opcion.EsCorrecta
                                                : false;

                                    const idPregunta = pregunta.idPregunta;
                                    const idOpcion = opcion.idOpcion || opcion.IdOpcion;
                                    const selectedId = respuestasUsuario[idPregunta];
                                    const permiteMultiple = pregunta.permiteMultiple;

                                    let isSelected = false;
                                    if (permiteMultiple && Array.isArray(selectedId)) {
                                        isSelected = selectedId.some(id => String(id) === String(idOpcion));
                                    } else {
                                        isSelected = selectedId && String(selectedId) === String(idOpcion);
                                    }

                                    let containerClass = "cc-option-item";
                                    let styleOverride = { cursor: 'pointer' };
                                    let letterStyle = {};

                                    if (examSubmitted) {
                                        styleOverride.cursor = 'default';
                                    }

                                    // Feedback Examen Opción Múltiple
                                    if (examSubmitted) {
                                        if (esCorrecta) {
                                            containerClass += " cc-option-correct";
                                        } else if (isSelected) {
                                            styleOverride.background = "#fef2f2";
                                            styleOverride.borderColor = "#ef4444";
                                            letterStyle = { borderColor: '#ef4444', color: '#ef4444', background: '#fff' };
                                        }
                                    } else if (isSelected) {
                                        // Durante el intento, mostrar verde si está seleccionado
                                        containerClass += " cc-option-correct";
                                    }

                                    return (
                                        <div
                                            key={opcion.idOpcion || opcion.IdOpcion || idx}
                                            className={containerClass}
                                            style={styleOverride}
                                            onClick={() =>
                                                !examSubmitted &&
                                                idPregunta &&
                                                idOpcion &&
                                                handleSelectOption(idPregunta, idOpcion, permiteMultiple)
                                            }
                                        >
                                            <span className="cc-option-letter" style={letterStyle}>{llave}</span>
                                            <span className="cc-option-text">{desc}</span>
                                            {examSubmitted && isSelected && esCorrecta && (
                                                <span className="cc-correct-badge">✓ Correcta</span>
                                            )}
                                            {examSubmitted && isSelected && !esCorrecta && (
                                                <span className="cc-correct-badge" style={{ background: '#ef4444' }}>✗ Incorrecta</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Función helper para verificar si un item es clickeable (basado en desbloqueo por secuencia)
    const isItemClickable = (item) => {
        if (!isItemUnlocked(item)) return false;

        // Regla adicional: si el certificado ya se generó, bloqueamos evaluaciones
        if ((item.tipo === 'EXAMEN' || item.tipo === 'CUESTIONARIO') && estudianteCursoRow?.certificadoGenerado) {
            return false;
        }

        // Regla adicional: si es un EXAMEN y ya se agotaron los intentos sin haber aprobado,
        // bloqueamos el acceso para que no pueda entrar al player a "repetir" o ver preguntas.
        if (item.tipo === 'EXAMEN') {
            const aprobado = examStatus.aprobados.includes(item.id);
            const intentos = allAttempts[`EXAMEN-${item.id}`] || 0;
            const limite = item.limiteIntentos || 3;
            if (!aprobado && limite > 0 && intentos >= limite) {
                return false;
            }
        }

        return true;
    };

    const getItemProgressKey = (item) => {
        const itemId = item.id || item.lessonId || item.quizId || item.examId;
        return `${item.tipo}-${itemId}`;
    };

    const isItemCompletedForUnlock = (item) => {
        // Para lecciones, el campo 'completado' es la fuente de verdad inmediata
        if (item.tipo === 'LECCION' && item.completado) return true;

        const progressKey = getItemProgressKey(item);
        const isApproved = (completedKeys || []).includes(progressKey);
        if (isApproved) return true;

        // Para CUESTIONARIOS: Bloquea el flujo hasta que se apruebe (Regla 1)
        if (item.tipo === 'CUESTIONARIO') {
            return false; // Solo isApproved (arriba) permite continuar
        }

        // Para EXAMENES: Solo se desbloquea si está aprobado O si agotó todos sus intentos
        if (item.tipo === 'EXAMEN') {
            const isFailed = (failedKeys || []).includes(progressKey);
            if (!isFailed) return false;

            const id = item.examId || item.id;
            const key = `EXAMEN-${id}`;
            const intentosRealizados = allAttempts[key] || 0;
            const limiteIntentos = item.limiteIntentos || 3;

            // Si el límite es 0 (ilimitado), y no ha aprobado, no desbloquea por "agotado"
            if (limiteIntentos === 0) return false;

            // Desbloquea si agotó los intentos disponibles
            return intentosRealizados >= limiteIntentos;
        }

        return false;
    };

    const isItemUnlocked = (item) => {
        if (!flatSidebarItems || flatSidebarItems.length === 0) return true;

        const itemIndex = flatSidebarItems.findIndex(
            (it) => it && it.tipo === item.tipo && String(it.id || it.lessonId || it.quizId || it.examId) === String(item.id || item.lessonId || item.quizId || item.examId)
        );

        // Si no se encuentra o es el primero, está desbloqueado
        if (itemIndex <= 0) return true;

        // El item está desbloqueado si el anterior está completado para desbloqueo
        const prevItem = flatSidebarItems[itemIndex - 1];
        if (!prevItem) return true;

        return isItemCompletedForUnlock(prevItem);
    };

    // Función helper para obtener el icono según el tipo
    const getIconForType = (tipo) => {
        switch (tipo) {
            case "LECCION":
                return FiPlayCircle;
            case "CUESTIONARIO":
                return FiHelpCircle;
            case "EXAMEN":
                return FiClipboard;
            default:
                return FiPlayCircle;
        }
    };

    // ---------- RENDER ----------

    return (
        <div className="cp-page">
            {/* TOPBAR */}
            <header className="cp-topbar">
                <button
                    type="button"
                    className="cp-topbar-close"
                    onClick={() => navigate(`/menu/lms/curso-estudiante/${slug}`)}
                    title="Volver al Curso"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '.5rem',
                        padding: '.35rem .65rem',
                        borderRadius: 999,
                    }}
                >
                    <FiArrowLeft />
                    Volver al Curso
                </button>

                <div className="cp-topbar-close cp-topbar-close-right" />
            </header>

            <div
                className={
                    "cp-layout" + (sidebarOpen ? "" : " cp-layout--collapsed")
                }
            >
                {/* SIDEBAR */}
                <div className="cp-sidebar-wrapper">
                    <aside className="cp-sidebar">
                        <div className="cp-sidebar-list">
                            {modulesWithLessons.map((mod) => (
                                <div key={mod.moduleId} className="cp-sidebar-module">
                                    <div className="cp-sidebar-module-header">
                                        <span className="cp-sidebar-module-title">
                                            {mod.moduleTitle}
                                        </span>
                                        <span className="cp-sidebar-module-count">
                                            {mod.items.length}
                                        </span>
                                    </div>

                                    <div className="cp-sidebar-module-lessons">
                                        {mod.items.map((item) => {
                                            const isActive =
                                                (item.tipo === "LECCION" &&
                                                    String(item.lessonId) === String(currentLessonId)) ||
                                                (item.tipo !== "LECCION" &&
                                                    currentContentType === item.tipo &&
                                                    String(currentContentId) === String(item.id));

                                            const Icon = getIconForType(item.tipo);
                                            const isClickable = isItemClickable(item);

                                            const itemId = item.lessonId || item.quizId || item.examId || item.id;
                                            const progressKey = `${item.tipo}-${String(itemId)}`;

                                            const isApproved = (completedKeys || []).includes(progressKey);
                                            const isFailed = (failedKeys || []).includes(progressKey);

                                            const itemFlatIndex = flatSidebarItems.findIndex(
                                                (it) => it && `${it.tipo}-${it.id}` === `${item.tipo}-${item.id}`
                                            );

                                            const isLockedBySequence = !isItemUnlocked(item);
                                            const isLockedByCertificate = (item.tipo === 'EXAMEN' || item.tipo === 'CUESTIONARIO') && estudianteCursoRow?.certificadoGenerado;
                                            const showLockIcon = isLockedBySequence || isLockedByCertificate;

                                            // Tooltip informativo
                                            let tooltipText = item.lessonTitle;
                                            if (isLockedBySequence) {
                                                tooltipText = `${item.lessonTitle}\n\nActividad bloqueada. Completa las anteriores para desbloquear.`;
                                            } else if (isLockedByCertificate) {
                                                tooltipText = `${item.lessonTitle}\n\nEvaluación bloqueada. Ya has generado tu certificado de este curso.`;
                                            } else {
                                                if (item.tipo === 'EXAMEN' || item.tipo === 'CUESTIONARIO') {
                                                    const id = item.tipo === 'EXAMEN' ? item.examId : item.quizId;
                                                    const key = `${item.tipo}-${id}`;
                                                    const intentosRealizados = allAttempts[key] || 0;
                                                    const limiteIntentos = item.limiteIntentos || 3;

                                                    if (limiteIntentos > 0 && intentosRealizados >= limiteIntentos) {
                                                        tooltipText = `${item.lessonTitle} (Límite de intentos alcanzado: ${intentosRealizados}/${limiteIntentos})`;
                                                    } else if (limiteIntentos > 0) {
                                                        tooltipText = `${item.lessonTitle} (Intentos: ${intentosRealizados}/${limiteIntentos})`;
                                                    }
                                                }

                                                if (isApproved && (item.tipo === 'EXAMEN' || item.tipo === 'CUESTIONARIO')) {
                                                    tooltipText = `${tooltipText}\n\nEstado: Aprobado`;
                                                } else if (isFailed && (item.tipo === 'EXAMEN' || item.tipo === 'CUESTIONARIO')) {
                                                    tooltipText = `${tooltipText}\n\nEstado: Reprobado`;
                                                }
                                            }

                                            return (
                                                <button
                                                    key={`${item.tipo}-${item.id}`}
                                                    type="button"
                                                    className={
                                                        "cp-sidebar-lesson" +
                                                        (isActive ? " active" : "") +
                                                        (item.completado ? " completed" : "") +
                                                        (isApproved ? " approved" : "") +
                                                        (isFailed ? " failed" : "") +
                                                        (!isClickable || isLockedBySequence ? " disabled" : "")
                                                    }
                                                    onClick={() =>
                                                        isClickable && !isLockedBySequence && handleClickContent(item)
                                                    }
                                                    disabled={!isClickable || isLockedBySequence}
                                                    title={tooltipText}
                                                    style={{
                                                        cursor: isClickable && !isLockedBySequence ? "pointer" : "not-allowed",
                                                        opacity: isClickable && !isLockedBySequence ? 1 : 0.5,
                                                    }}
                                                >
                                                    <span className="cp-sidebar-lesson-icon">
                                                        {showLockIcon ? (
                                                            <FiLock />
                                                        ) : item.completado && item.tipo === "LECCION" ? (
                                                            <FiCheckCircle />
                                                        ) : isApproved && (item.tipo === 'EXAMEN' || item.tipo === 'CUESTIONARIO') ? (
                                                            <FiCheckCircle />
                                                        ) : isFailed && (item.tipo === 'EXAMEN' || item.tipo === 'CUESTIONARIO') ? (
                                                            <FiXCircle />
                                                        ) : (
                                                            <Icon />
                                                        )}
                                                    </span>
                                                    <span className="cp-sidebar-lesson-title">
                                                        {item.lessonTitle}
                                                        {!isClickable && (item.tipo === 'EXAMEN' || item.tipo === 'CUESTIONARIO') && (
                                                            <span style={{
                                                                marginLeft: '0.5rem',
                                                                fontSize: '0.75rem',
                                                                color: 'var(--color-primary)',
                                                                fontWeight: 'bold'
                                                            }}>
                                                                🔒
                                                            </span>
                                                        )}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            {modulesWithLessons.length === 0 && (
                                <p className="cp-sidebar-empty">
                                    Este curso aún no tiene lecciones configuradas.
                                </p>
                            )}
                        </div>
                    </aside>

                    <button
                        type="button"
                        className={
                            "cp-sidebar-toggle" +
                            (sidebarOpen ? "" : " cp-sidebar-toggle--collapsed")
                        }
                        onClick={() => setSidebarOpen((prev) => !prev)}
                        aria-label={
                            sidebarOpen
                                ? "Ocultar lista de lecciones"
                                : "Mostrar lista de lecciones"
                        }
                    >
                        <span className="cp-sidebar-toggle-icon">
                            {sidebarOpen ? "‹" : "›"}
                        </span>
                    </button>
                </div>

                {/* MAIN */}
                <main className="cp-main">
                    {navBlockMessage && (
                        <div
                            style={{
                                marginBottom: '1rem',
                                padding: '.75rem 1rem',
                                borderRadius: 8,
                                backgroundColor: '#fff7ed',
                                border: '1px solid #fed7aa',
                                color: '#9a3412',
                                fontWeight: 600,
                            }}
                        >
                            {navBlockMessage}
                        </div>
                    )}
                    {justCompletedTitle && (
                        <div className="cp-alert-success">
                            ¡Enhorabuena! Has completado «{justCompletedTitle}».
                        </div>
                    )}

                    {currentContentType === "CUESTIONARIO" ? (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <h1 className="cp-lesson-title" style={{ marginBottom: 0 }}>
                                    {(() => {
                                        // Buscar título real en los módulos
                                        const active = (sidebarModules || []).flatMap(m => m.items || []).find(i => String(i.lessonId || i.quizId || i.examId || i.id) === String(currentContentId) && i.tipo === currentContentType);
                                        return active?.lessonTitle || currentContentTitle || "Cuestionario";
                                    })()}
                                </h1>
                                {(() => {
                                    const limiteIntentos = currentQuiz?.limiteIntentos || currentQuiz?.LimiteIntentos || 3;
                                    if (limiteIntentos > 0) {
                                        const bloqueado = quizAttempts >= limiteIntentos;
                                        return (
                                            <span style={{
                                                fontSize: "0.9rem",
                                                color: bloqueado ? "#991b1b" : "#334155",
                                                fontWeight: 500,
                                                backgroundColor: bloqueado ? "#fee2e2" : "#e2e8f0",
                                                padding: "0.25rem 0.75rem",
                                                borderRadius: "6px"
                                            }}>
                                                {bloqueado
                                                    ? `Intentos: ${quizAttempts} de ${limiteIntentos}`
                                                    : `Intento ${quizAttempts + 1} de ${limiteIntentos}`
                                                }
                                            </span>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                            <p className="cp-lesson-subtitle">
                                Preguntas de evaluación del curso
                            </p>
                            <div
                                style={{
                                    marginTop: "1.5rem",
                                    marginBottom: "2rem",
                                    boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                    borderRadius: "8px",
                                    padding: "1rem 1.25rem",
                                    backgroundColor: "#f8fafc",
                                }}
                            >
                                {renderQuizView()}
                            </div>
                            <div className="cp-lesson-actions">
                                {(() => {
                                    const limiteIntentos = currentQuiz?.limiteIntentos || currentQuiz?.LimiteIntentos || 3;
                                    const alcanzadoLimite = quizAttempts >= limiteIntentos;

                                    if (alcanzadoLimite) {
                                        return (
                                            <div style={{
                                                padding: "1rem",
                                                backgroundColor: "#fef2f2",
                                                border: "1px solid #fecaca",
                                                borderRadius: "8px",
                                                color: "#991b1b",
                                                textAlign: "center"
                                            }}>
                                                <strong>Límite de intentos alcanzado</strong>
                                                <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.9rem" }}>
                                                    Has realizado {quizAttempts} de {limiteIntentos} intentos permitidos.
                                                </p>
                                            </div>
                                        );
                                    }

                                    return (
                                        <>
                                            {quizSubmitted && !resultsData.aprobado && (currentQuiz?.limiteIntentos || currentQuiz?.LimiteIntentos || 3) - quizAttempts > 0 ? (
                                                <button
                                                    type="button"
                                                    className="cp-nav-btn"
                                                    style={{ backgroundColor: 'var(--color-primary, #c92a2a)' }}
                                                    onClick={handleQuizButtonClick}
                                                >
                                                    Volver a intentar <FiPlayCircle />
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="cp-nav-btn"
                                                    onClick={handleQuizButtonClick}
                                                    disabled={quizPreguntas.length === 0}
                                                >
                                                    {!quizSubmitted ? 'Enviar Cuestionario' : 'Siguiente'} <FiChevronRight />
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </>
                    ) : currentContentType === "EXAMEN" ? (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                <h1 className="cp-lesson-title" style={{ marginBottom: 0 }}>
                                    {(() => {
                                        // Buscar título real en los módulos
                                        const active = (sidebarModules || []).flatMap(m => m.items || []).find(i => String(i.lessonId || i.quizId || i.examId || i.id) === String(currentContentId) && i.tipo === currentContentType);
                                        return active?.lessonTitle || currentContentTitle || "Examen";
                                    })()}
                                </h1>
                                {(() => {
                                    const limiteIntentos = currentExam?.limiteIntentos || currentExam?.LimiteIntentos || 3;
                                    if (limiteIntentos > 0) {
                                        const bloqueado = examAttempts >= limiteIntentos;
                                        return (
                                            <span style={{
                                                fontSize: "0.9rem",
                                                color: bloqueado ? "#991b1b" : "#334155",
                                                fontWeight: 500,
                                                backgroundColor: bloqueado ? "#fee2e2" : "#e2e8f0",
                                                padding: "0.25rem 0.75rem",
                                                borderRadius: "6px"
                                            }}>
                                                {bloqueado
                                                    ? `Intentos: ${examAttempts} de ${limiteIntentos}`
                                                    : `Intento ${examAttempts + 1} de ${limiteIntentos}`
                                                }
                                            </span>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                            <p className="cp-lesson-subtitle">
                                Preguntas del examen del curso
                            </p>
                            <div
                                style={{
                                    marginTop: "1.5rem",
                                    marginBottom: "2rem",
                                    boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                    borderRadius: "8px",
                                    padding: "1rem 1.25rem",
                                    backgroundColor: "#f8fafc",
                                }}
                            >
                                {renderExamView()}
                            </div>
                            <div className="cp-lesson-actions">
                                {(() => {
                                    const limiteIntentos = currentExam?.limiteIntentos || currentExam?.LimiteIntentos || 3;
                                    const alcanzadoLimite = examAttempts >= limiteIntentos;

                                    if (alcanzadoLimite) {
                                        return (
                                            <div style={{
                                                padding: "1rem",
                                                backgroundColor: "#fef2f2",
                                                border: "1px solid #fecaca",
                                                borderRadius: "8px",
                                                color: "#991b1b",
                                                textAlign: "center"
                                            }}>
                                                <strong>Límite de intentos alcanzado</strong>
                                                <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.9rem" }}>
                                                    Has realizado {examAttempts} de {limiteIntentos} intentos permitidos.
                                                </p>
                                            </div>
                                        );
                                    }

                                    return (
                                        <>
                                            {examSubmitted && !resultsData.aprobado && (currentExam?.limiteIntentos || currentExam?.LimiteIntentos || 3) - examAttempts > 0 ? (
                                                <button
                                                    type="button"
                                                    className="cp-nav-btn"
                                                    style={{ backgroundColor: 'var(--color-primary, #c92a2a)' }}
                                                    onClick={handleExamButtonClick}
                                                >
                                                    Volver a intentar <FiPlayCircle />
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="cp-nav-btn"
                                                    onClick={handleExamButtonClick}
                                                    disabled={examPreguntas.length === 0}
                                                >
                                                    {!examSubmitted ? 'Enviar Examen' : 'Siguiente'} <FiChevronRight />
                                                </button>
                                            )}
                                        </>
                                    );
                                })()}
                            </div>
                        </>
                    ) : lessonSections && lessonSections.length > 0 ? (
                        <>
                            <h1 className="cp-lesson-title">
                                {currentItem?.lessonTitle || "Lección"}
                            </h1>

                            {currentItem && (
                                <p className="cp-lesson-subtitle">
                                    {currentItem.moduleTitle} · {currentSidebarIndex + 1} de{" "}
                                    {totalElements} elementos
                                </p>
                            )}

                            {/* Contenido real de la lección desde BD */}
                            <div style={{
                                marginTop: "1.5rem",
                                marginBottom: "2rem",
                                boxShadow: "0 1px 3px rgba(15,23,42,0.08)",
                                borderRadius: "8px",
                                overflow: "hidden"
                            }}>
                                {renderLessonSections()}
                            </div>

                            {currentItem && (
                                <>
                                    <div className="cp-lesson-nav">
                                        <button
                                            type="button"
                                            className="cp-nav-btn"
                                            disabled={currentSidebarIndex <= 0}
                                            onClick={goPrev}
                                        >
                                            <FiChevronLeft /> Anterior
                                        </button>
                                        <button
                                            type="button"
                                            className="cp-nav-btn"
                                            onClick={goNext}
                                            disabled={!allVideosCompleted}
                                            title={!allVideosCompleted ? "Debes terminar de ver los videos para continuar" : ""}
                                        >
                                            {allVideosCompleted ? 'Siguiente' : 'Mira los videos para continuar'} <FiChevronRight />
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="cp-empty-main">
                            <p>No hay lecciones seleccionadas en este curso todavía.</p>
                        </div>
                    )}
                </main>
            </div>
            {showResultsModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '2rem',
                        width: '90%',
                        maxWidth: '400px',
                        textAlign: 'center',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }}>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b', marginBottom: '1.5rem' }}>
                            Resultados
                        </h2>

                        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', marginBottom: '2rem' }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#22c55e' }}>
                                    {resultsData.correctas}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Correctas</div>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '2rem', fontWeight: '700', color: '#ef4444' }}>
                                    {resultsData.incorrectas}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Incorrectas</div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '2rem' }}>
                            <div style={{
                                fontSize: '4rem',
                                fontWeight: '800',
                                color: resultsData.aprobado ? '#22c55e' : '#ef4444',
                                lineHeight: 1
                            }}>
                                {resultsData.calificacion}
                            </div>
                            <div style={{
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                color: resultsData.aprobado ? '#22c55e' : '#ef4444',
                                marginTop: '0.5rem'
                            }}>
                                {resultsData.aprobado ? '¡Aprobado!' : 'No aprobado'}
                            </div>
                        </div>

                        <button
                            onClick={() => setShowResultsModal(false)}
                            style={{
                                backgroundColor: '#0f172a',
                                color: 'white',
                                padding: '0.75rem 2rem',
                                borderRadius: '8px',
                                border: 'none',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                width: '100%',
                                transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#1e293b'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#0f172a'}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
            <AlertDialog
                open={alertDialog.open}
                onClose={() => setAlertDialog({ ...alertDialog, open: false })}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
            />
        </div>
    );
};

export default CoursePlayerStudent;
